import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import {
  paymentsApi,
  Payment,
  LedgerTransactionLink,
  incomingPaymentsApi,
  IncomingPayment,
  IncomingFeeQuote,
  oftIncomingApi,
  OftIncoming,
  SaveIncomingDraftRequest,
  SaveOftIncomingDraftRequest,
} from '../api/payments';
import { X, UserCheck, Link2, PanelRightOpen, Search } from 'lucide-react';
import { useSelectedClientStore } from '../store/selectedClientStore';
import { getErrorMessage } from '../api/client';
import { notifyError, notifyErrorMessage, notifySuccess } from '../utils/notify';
import { formatPublicIdDisplay } from '../utils/formatPublicId';
import { getClientDisplayName, getClientOptionLabel } from '../utils/clientDisplay';
import OutgoingPaymentDetailModal from '../components/OutgoingPaymentDetailModal';
import IncomingPaymentDetailModal from '../components/IncomingPaymentDetailModal';
import PaymentReturnsPanel from '../components/PaymentReturnsPanel';
import { adminApi, AccountRequisites, Client, ClientAccountListItem, CurrencyDto } from '../api/admin';
import { currencyCountriesApi, CountryListItem } from '../api/currencyCountries';
import { providersApi, Provider } from '../api/providers';
import { useAdminAuthStore } from '../store/adminAuthStore';
import EnglishDatePicker from '../components/common/EnglishDatePicker';

const PAYMENT_PURPOSE_OPTIONS = [
  { label: 'Business expenses', value: 'BussinessExpenses', category: 1 },
  { label: 'Construction', value: 'Construction', category: 2 },
  { label: 'Education/training', value: 'Education', category: 3 },
  { label: 'Freight', value: 'Freight', category: 4 },
  { label: 'Goods purchase', value: 'GoodsPurchase', category: 5 },
  { label: 'Investment capital', value: 'InvestmentCapital', category: 6 },
  { label: 'Living expenses', value: 'LivingExpenses', category: 7 },
  { label: 'Loan/credit payment', value: 'LoanCreditPayment', category: 8 },
  { label: 'Medical services', value: 'MedicalServices', category: 9 },
  { label: 'Pension', value: 'Pension', category: 10 },
  { label: 'Professional services', value: 'ProfessionalServices', category: 11 },
  { label: 'Real estate', value: 'RealEstate', category: 12 },
  { label: 'Taxes', value: 'Taxes', category: 13 },
  { label: 'Wages/Salary', value: 'WagesSalary', category: 14 },
  { label: 'Other services', value: 'Other', category: 15 },
] as const;

type PaymentPurposeValue = (typeof PAYMENT_PURPOSE_OPTIONS)[number]['value'];

type InternalRecipientCompatibility = {
  kind: 'idle' | 'needs_source' | 'checking' | 'compatible' | 'incompatible';
  message: string;
};

const purposeCategoryForValue = (value: PaymentPurposeValue) =>
  PAYMENT_PURPOSE_OPTIONS.find((x) => x.value === value)?.category ?? 5;

const isPaymentPurposeValue = (value: string): value is PaymentPurposeValue =>
  PAYMENT_PURPOSE_OPTIONS.some((x) => x.value === value);

const confirmationFileName = (contentDisposition: unknown, paymentId: string): string => {
  const header = String(contentDisposition ?? '');
  const encoded = /filename\*=UTF-8''([^;]+)|filename\*=(?:"?)([^";]+)/i.exec(header);
  const plain = /filename=(?:"([^"]+)"|([^;\s]+))/i.exec(header);
  const value = encoded?.[1] || encoded?.[2] || plain?.[1] || plain?.[2];

  if (value) {
    try {
      return decodeURIComponent(value.trim());
    } catch {
      return value.trim();
    }
  }

  return `XChangeLab_Payment_Confirmation_${paymentId}.pdf`;
};

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PENDING: 'bg-amber-100 text-amber-700',
  PROCESSING: 'bg-blue-100 text-blue-700',
  SUBMITTED: 'bg-yellow-100 text-yellow-700',
  UNDER_REVIEW: 'bg-orange-100 text-orange-700',
  PENDING_CREDITING: 'bg-sky-100 text-sky-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  ON_HOLD: 'bg-red-100 text-red-700',
  SENT: 'bg-green-100 text-green-700',
  CREDITED: 'bg-emerald-100 text-emerald-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700',
  FAILED: 'bg-red-200 text-red-800',
  REJECTED: 'bg-red-200 text-red-800',
  CANCELLED: 'bg-gray-200 text-gray-600',
};

const INCOMING_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  UNDER_REVIEW: 'Under review',
  PENDING_CREDITING: 'Pending crediting',
  CREDITED: 'Credited',
  REJECTED: 'Rejected',
  DRAFT: 'Draft',
};

function formatIncomingStatus(status: string | null | undefined): string {
  const key = (status || '').toUpperCase();
  return INCOMING_STATUS_LABELS[key] || status || '—';
}

function canComplianceIncomingActions(role: string | undefined): boolean {
  return role === 'SuperAdmin' || role === 'Admin' || role === 'Compliance';
}

function canOperationsIncomingActions(role: string | undefined): boolean {
  return role === 'SuperAdmin' || role === 'Admin' || role === 'Operations';
}

function canRejectIncomingPayment(role: string | undefined): boolean {
  return canComplianceIncomingActions(role) || canOperationsIncomingActions(role);
}

function canOperationsOutgoingActions(role: string | undefined): boolean {
  return role === 'SuperAdmin' || role === 'Admin' || role === 'Operations';
}

function canApprovePaymentReturns(role: string | undefined): boolean {
  return role === 'SuperAdmin' || role === 'Admin';
}

function canDeleteOutgoingDraft(
  role: string | undefined,
  adminUserId: string | undefined,
  createdByUserId: string | undefined,
): boolean {
  if (!role || !adminUserId) return false;
  if (role === 'Compliance' || role === 'Viewer') return false;
  if (createdByUserId && createdByUserId === adminUserId) {
    return canOperationsOutgoingActions(role);
  }
  return canOperationsOutgoingActions(role);
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function PaymentsPage() {
  const { selectedClient, clearSelectedClient } = useSelectedClientStore();
  const adminRole = useAdminAuthStore((state) => state.adminUser?.role);
  const adminUserId = useAdminAuthStore((state) => state.adminUser?.id);

  const [payments, setPayments] = useState<Payment[]>([]);
  const [incomingPayments, setIncomingPayments] = useState<IncomingPayment[]>([]);
  const [oftIncomingPayments, setOftIncomingPayments] = useState<OftIncoming[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentFlowTab, setPaymentFlowTab] = useState<'outgoing' | 'incoming' | 'digital_outgoing' | 'digital_incoming' | 'returns'>('outgoing');
  const [listTab, setListTab] = useState<'processing' | 'drafts'>('processing');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterKind, setFilterKind] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');
  const [incomingFilterStatus, setIncomingFilterStatus] = useState('all');
  const [incomingFilterKind, setIncomingFilterKind] = useState('all');
  const [incomingFilterMethod, setIncomingFilterMethod] = useState('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Reject modal
  const [rejectModal, setRejectModal] = useState<{ id: string; open: boolean }>({ id: '', open: false });
  const [rejectReason, setRejectReason] = useState('');
  const [rejectComment, setRejectComment] = useState('');
  const [incomingRejectModal, setIncomingRejectModal] = useState<{ id: string; open: boolean }>({ id: '', open: false });
  const [incomingRejectReason, setIncomingRejectReason] = useState('');

  // Hold modal
  const [holdModal, setHoldModal] = useState<{ id: string; open: boolean }>({ id: '', open: false });
  const [holdReason, setHoldReason] = useState('');
  const [holdComment, setHoldComment] = useState('');

  // Cancel (back-office) modal — returns principal + fee to the client
  const [cancelModal, setCancelModal] = useState<{ id: string; open: boolean }>({ id: '', open: false });
  const [cancelReason, setCancelReason] = useState('');

  // Ledger Links modal (Audit Trail)
  const [linksModal, setLinksModal] = useState<{ id: string; open: boolean }>({ id: '', open: false });
  const [ledgerLinks, setLedgerLinks] = useState<LedgerTransactionLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);

  const [detailPaymentId, setDetailPaymentId] = useState<string | null>(null);
  const [returnOriginalPaymentId, setReturnOriginalPaymentId] = useState<string | undefined>();
  const [incomingDetailId, setIncomingDetailId] = useState<string | null>(null);
  const [incomingDetailSource, setIncomingDetailSource] = useState<'incoming' | 'oft'>('incoming');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createTab, setCreateTab] = useState<'outgoing' | 'incoming' | 'digital_outgoing' | 'digital_incoming'>('outgoing');
  const [incomingMode, setIncomingMode] = useState<'matched' | 'unmatched'>('matched');
  const [createLoading, setCreateLoading] = useState(false);
  const [editingDraftPaymentId, setEditingDraftPaymentId] = useState<string | null>(null);
  const [editingIncomingDraftId, setEditingIncomingDraftId] = useState<string | null>(null);
  const [incomingFeeQuote, setIncomingFeeQuote] = useState<IncomingFeeQuote | null>(null);
  const [incomingFeeQuoteLoading, setIncomingFeeQuoteLoading] = useState(false);
  const [incomingOverrideFeeAmount, setIncomingOverrideFeeAmount] = useState('');
  const [incomingOverrideFeeReason, setIncomingOverrideFeeReason] = useState('');
  const incomingFeeQuoteDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [clients, setClients] = useState<Client[]>([]);
  const [countries, setCountries] = useState<CountryListItem[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyDto[]>([]);
  const [outgoingAccounts, setOutgoingAccounts] = useState<ClientAccountListItem[]>([]);
  const [incomingAccounts, setIncomingAccounts] = useState<ClientAccountListItem[]>([]);
  const [incomingRequisites, setIncomingRequisites] = useState<AccountRequisites[]>([]);
  const [recipientLookupQuery, setRecipientLookupQuery] = useState('');
  const [recipientLookupOpen, setRecipientLookupOpen] = useState(false);
  const [recipientLookupLoading, setRecipientLookupLoading] = useState(false);
  const [recipientLookupResults, setRecipientLookupResults] = useState<Client[]>([]);
  const [recipientAccountsByClientId, setRecipientAccountsByClientId] = useState<Record<string, ClientAccountListItem[]>>({});
  const [recipientAccountLoadingByClientId, setRecipientAccountLoadingByClientId] = useState<Record<string, boolean>>({});
  const recipientLookupDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recipientLookupRequestSeqRef = useRef(0);
  const recipientLookupContainerRef = useRef<HTMLDivElement | null>(null);
  const recipientAccountsByClientIdRef = useRef<Record<string, ClientAccountListItem[]>>({});
  const recipientAccountLoadingByClientIdRef = useRef<Record<string, boolean>>({});

  const [outgoingForm, setOutgoingForm] = useState({
    clientId: selectedClient?.id ?? '',
    fromAccountId: '',
    paymentCurrencyId: '',
    amount: '',
    paymentDate: '',
    purpose: 'Other' as PaymentPurposeValue,
    paymentReference: '',
    paymentKind: 'EXTERNAL' as 'EXTERNAL' | 'INTERNAL' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING',
    recipientClientId: '',
    beneficiaryName: '',
    beneficiaryBankName: '',
    beneficiaryAccountNumber: '',
    beneficiarySwift: '',
    recipientCountryId: '',
    bankCountryId: '',
    beneficiaryCity: '',
    beneficiaryZipCode: '',
    beneficiaryAddress: '',
  });

  const [incomingForm, setIncomingForm] = useState({
    clientId: selectedClient?.id ?? '',
    clientAccountId: '',
    providerId: '',
    currencyId: '',
    paymentKind: 'EXTERNAL' as 'EXTERNAL' | 'INTERNAL' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING',
    amount: '',
    paymentDate: '',
    paymentReference: '',
    beneficiaryName: '',
    beneficiaryBankName: '',
    beneficiaryAccountNumber: '',
    beneficiarySwift: '',
    recipientCountryId: '',
    bankCountryId: '',
    beneficiaryCity: '',
    beneficiaryZipCode: '',
    beneficiaryAddress: '',
    backofficeComment: '',
    paymentMethod: 'SWIFT' as 'SWIFT' | 'SEPA',
    receivedOnRequisiteId: '',
    methodMismatchReason: '',
  });

  const isAccountActive = useCallback(
    (account: ClientAccountListItem) => (account.status || '').trim().toUpperCase() === 'ACTIVE',
    []
  );

  const loadClientAccounts = useCallback(
    async (clientId: string, target: 'outgoing' | 'incoming') => {
      if (!clientId) {
        if (target === 'outgoing') setOutgoingAccounts([]);
        else setIncomingAccounts([]);
        return;
      }
      try {
        const response = await adminApi.getClientAccountsByClientId(clientId);
        const activeAccounts = (response.data || []).filter(isAccountActive);
        if (target === 'outgoing') setOutgoingAccounts(activeAccounts);
        else setIncomingAccounts(activeAccounts);
      } catch (err: unknown) {
        notifyError(err, 'Failed to load client accounts');
        if (target === 'outgoing') setOutgoingAccounts([]);
        else setIncomingAccounts([]);
      }
    },
    [isAccountActive]
  );

  useEffect(() => {
    fetchPayments();
    fetchIncomingPayments();
    fetchOftIncomingPayments();
    loadClients();
    loadCountries();
    loadProviders();
    loadCurrencies();
  }, []);

  useEffect(() => {
    if (!selectedClient?.id) {
      setOutgoingForm((prev) => ({
        ...prev,
        clientId: '',
        fromAccountId: '',
        paymentCurrencyId: '',
      }));
      setIncomingForm((prev) => ({
        ...prev,
        clientId: '',
        clientAccountId: '',
      }));
      return;
    }
    setOutgoingForm((prev) => ({
      ...prev,
      clientId: selectedClient.id,
      fromAccountId: prev.clientId === selectedClient.id ? prev.fromAccountId : '',
      paymentCurrencyId: prev.clientId === selectedClient.id ? prev.paymentCurrencyId : '',
    }));
    setIncomingForm((prev) => ({
      ...prev,
      clientId: selectedClient.id,
      clientAccountId: prev.clientId === selectedClient.id ? prev.clientAccountId : '',
    }));
  }, [selectedClient?.id]);

  useEffect(() => {
    const accountId = incomingForm.clientAccountId;
    if (!accountId || incomingMode !== 'matched' || createTab === 'digital_incoming') {
      setIncomingRequisites([]);
      return;
    }

    let cancelled = false;
    void adminApi.getAccountRequisites(accountId, true)
      .then((res) => {
        if (!cancelled) setIncomingRequisites(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setIncomingRequisites([]);
      });

    return () => {
      cancelled = true;
    };
  }, [createTab, incomingForm.clientAccountId, incomingMode]);

  useEffect(() => {
    void loadClientAccounts(outgoingForm.clientId, 'outgoing');
  }, [outgoingForm.clientId, loadClientAccounts]);

  useEffect(() => {
    if (!outgoingForm.fromAccountId) return;
    const selected = outgoingAccounts.find((a) => a.id === outgoingForm.fromAccountId);
    if (!selected) return;
    if (outgoingForm.paymentCurrencyId !== selected.currencyId) {
      setOutgoingForm((prev) => ({ ...prev, paymentCurrencyId: selected.currencyId }));
    }
  }, [outgoingForm.fromAccountId, outgoingAccounts, outgoingForm.paymentCurrencyId]);

  const currencyCodeByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of currencies) {
      if (!c?.id || !c?.code) continue;
      map.set(c.id.toUpperCase(), c.code.toUpperCase());
      map.set(c.code.toUpperCase(), c.code.toUpperCase());
    }
    return map;
  }, [currencies]);

  const resolveCurrencyLabel = useCallback((rawCurrencyId: string | null | undefined) => {
    const raw = (rawCurrencyId || '').trim();
    if (!raw) return '';
    const byKey = currencyCodeByKey.get(raw.toUpperCase());
    if (byKey) return byKey;
    // Avoid showing UUID in UI when dictionary missed this value.
    return UUID_REGEX.test(raw) ? '' : raw.toUpperCase();
  }, [currencyCodeByKey]);

  const outgoingCurrencyOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const account of outgoingAccounts) {
      const code = resolveCurrencyLabel(account.currencyId);
      if (!code) continue;
      if (!map.has(account.currencyId)) map.set(account.currencyId, code);
    }
    return Array.from(map.entries()).map(([id, code]) => ({ id, code }));
  }, [outgoingAccounts, resolveCurrencyLabel]);

  const filteredOutgoingAccounts = useMemo(() => {
    if (!outgoingForm.paymentCurrencyId) return outgoingAccounts;
    return outgoingAccounts.filter((a) => a.currencyId === outgoingForm.paymentCurrencyId);
  }, [outgoingAccounts, outgoingForm.paymentCurrencyId]);

  const selectedOutgoingSourceAccount = useMemo(
    () => outgoingAccounts.find((account) => account.id === outgoingForm.fromAccountId) ?? null,
    [outgoingAccounts, outgoingForm.fromAccountId]
  );

  const ensureRecipientAccountsLoaded = useCallback(async (clientId: string) => {
    const normalizedClientId = clientId.trim();
    if (!normalizedClientId) return [] as ClientAccountListItem[];
    const cachedAccounts = recipientAccountsByClientIdRef.current[normalizedClientId];
    if (cachedAccounts) return cachedAccounts;
    if (recipientAccountLoadingByClientIdRef.current[normalizedClientId]) {
      return [] as ClientAccountListItem[];
    }

    setRecipientAccountLoadingByClientId((prev) => ({ ...prev, [normalizedClientId]: true }));
    try {
      const response = await adminApi.getClientAccountsByClientId(normalizedClientId);
      const activeAccounts = (response.data || []).filter(isAccountActive);
      setRecipientAccountsByClientId((prev) => ({ ...prev, [normalizedClientId]: activeAccounts }));
      return activeAccounts;
    } catch {
      setRecipientAccountsByClientId((prev) => ({ ...prev, [normalizedClientId]: [] }));
      return [] as ClientAccountListItem[];
    } finally {
      setRecipientAccountLoadingByClientId((prev) => ({ ...prev, [normalizedClientId]: false }));
    }
  }, [isAccountActive]);

  useEffect(() => {
    recipientAccountsByClientIdRef.current = recipientAccountsByClientId;
  }, [recipientAccountsByClientId]);

  useEffect(() => {
    recipientAccountLoadingByClientIdRef.current = recipientAccountLoadingByClientId;
  }, [recipientAccountLoadingByClientId]);

  useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (!recipientLookupContainerRef.current) return;
      if (!recipientLookupContainerRef.current.contains(event.target as Node)) {
        setRecipientLookupOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, []);

  useEffect(() => {
    return () => {
      if (recipientLookupDebounceRef.current) {
        clearTimeout(recipientLookupDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (outgoingForm.paymentKind !== 'INTERNAL') {
      setRecipientLookupOpen(false);
      setRecipientLookupQuery('');
      setRecipientLookupLoading(false);
      setRecipientLookupResults([]);
      return;
    }

    if (outgoingForm.recipientClientId && outgoingForm.recipientClientId === outgoingForm.clientId) {
      setOutgoingForm((prev) => ({ ...prev, recipientClientId: '' }));
    }
  }, [outgoingForm.clientId, outgoingForm.paymentKind, outgoingForm.recipientClientId]);

  useEffect(() => {
    if (outgoingForm.paymentKind !== 'INTERNAL' || !recipientLookupOpen) return;

    const query = recipientLookupQuery.trim();
    if (recipientLookupDebounceRef.current) {
      clearTimeout(recipientLookupDebounceRef.current);
      recipientLookupDebounceRef.current = null;
    }

    if (query.length < 2) {
      setRecipientLookupLoading(false);
      setRecipientLookupResults([]);
      return;
    }

    const requestSeq = ++recipientLookupRequestSeqRef.current;
    setRecipientLookupLoading(true);
    recipientLookupDebounceRef.current = setTimeout(() => {
      void (async () => {
        try {
          const response = await adminApi.searchClients(query);
          const raw: Client[] = Array.isArray(response.data)
            ? (response.data as Client[])
            : ((response.data as { data?: Client[] })?.data ?? []);
          const filtered = raw.filter((client) => client?.id && client.id !== outgoingForm.clientId);
          if (recipientLookupRequestSeqRef.current !== requestSeq) return;
          setRecipientLookupResults(filtered);
          void Promise.all(filtered.slice(0, 8).map((client) => ensureRecipientAccountsLoaded(client.id)));
        } catch {
          if (recipientLookupRequestSeqRef.current !== requestSeq) return;
          setRecipientLookupResults([]);
        } finally {
          if (recipientLookupRequestSeqRef.current === requestSeq) {
            setRecipientLookupLoading(false);
          }
        }
      })();
    }, 300);
  }, [
    ensureRecipientAccountsLoaded,
    outgoingForm.clientId,
    outgoingForm.paymentKind,
    recipientLookupOpen,
    recipientLookupQuery,
  ]);

  useEffect(() => {
    if (outgoingForm.paymentKind !== 'INTERNAL' || !outgoingForm.recipientClientId) return;
    if (recipientAccountsByClientId[outgoingForm.recipientClientId]) return;
    void ensureRecipientAccountsLoaded(outgoingForm.recipientClientId);
  }, [ensureRecipientAccountsLoaded, outgoingForm.paymentKind, outgoingForm.recipientClientId, recipientAccountsByClientId]);

  useEffect(() => {
    void loadClientAccounts(incomingForm.clientId, 'incoming');
  }, [incomingForm.clientId, loadClientAccounts]);

  useEffect(() => {
    if (!createModalOpen || (createTab !== 'incoming' && createTab !== 'digital_incoming')) {
      return;
    }

    const amount = Number(incomingForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setIncomingFeeQuote(null);
      return;
    }

    if (incomingFeeQuoteDebounceRef.current) {
      clearTimeout(incomingFeeQuoteDebounceRef.current);
    }

    incomingFeeQuoteDebounceRef.current = setTimeout(() => {
      void (async () => {
        const isOft = createTab === 'digital_incoming';
        if (isOft) {
          if (!incomingForm.clientId || !incomingForm.clientAccountId) {
            setIncomingFeeQuote(null);
            return;
          }
        } else if (incomingMode === 'matched') {
          if (!incomingForm.clientId || !incomingForm.clientAccountId) {
            setIncomingFeeQuote(null);
            return;
          }
        } else if (!incomingForm.providerId || !incomingForm.currencyId) {
          setIncomingFeeQuote(null);
          return;
        }

        try {
          setIncomingFeeQuoteLoading(true);
          const api = isOft ? oftIncomingApi : incomingPaymentsApi;
          const selectedAccount = incomingAccounts.find((a) => a.id === incomingForm.clientAccountId);
          const params = isOft || incomingMode === 'matched'
            ? {
                clientId: incomingForm.clientId || undefined,
                providerId: selectedAccount?.providerId,
                clientAccountId: incomingForm.clientAccountId || undefined,
                amount,
                currencyId: selectedAccount?.currencyId,
                paymentKind: incomingForm.paymentKind,
                paymentMethod: incomingForm.paymentMethod,
              }
            : {
                providerId: incomingForm.providerId || undefined,
                amount,
                currencyId: incomingForm.currencyId || undefined,
                paymentKind: incomingForm.paymentKind,
                paymentMethod: incomingForm.paymentMethod,
              };
          const response = await api.feeQuote(params);
          setIncomingFeeQuote(response.data);
        } catch {
          setIncomingFeeQuote(null);
        } finally {
          setIncomingFeeQuoteLoading(false);
        }
      })();
    }, 400);

    return () => {
      if (incomingFeeQuoteDebounceRef.current) {
        clearTimeout(incomingFeeQuoteDebounceRef.current);
      }
    };
  }, [
    createModalOpen,
    createTab,
    incomingForm.amount,
    incomingForm.clientId,
    incomingForm.clientAccountId,
    incomingForm.providerId,
    incomingForm.currencyId,
    incomingForm.paymentKind,
    incomingForm.paymentMethod,
    incomingMode,
    incomingAccounts,
  ]);

  useEffect(() => {
    if (incomingForm.clientId && incomingMode === 'unmatched') {
      setIncomingMode('matched');
    }
  }, [incomingForm.clientId, incomingMode]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await paymentsApi.getAll();
      setPayments(response.data || []);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch payments'));
    } finally {
      setLoading(false);
    }
  };

  const fetchIncomingPayments = async () => {
    try {
      const response = await incomingPaymentsApi.list();
      setIncomingPayments(response.data || []);
    } catch (err: unknown) {
      notifyError(err, 'Failed to fetch incoming payments');
    }
  };

  const fetchOftIncomingPayments = async () => {
    try {
      const response = await oftIncomingApi.list();
      setOftIncomingPayments(response.data || []);
    } catch (err: unknown) {
      notifyError(err, 'Failed to fetch OFT incoming payments');
    }
  };

  const refreshIncomingLists = async () => {
    await Promise.all([fetchIncomingPayments(), fetchOftIncomingPayments()]);
  };

  const loadClients = async () => {
    try {
      const response = await adminApi.getClients();
      setClients(response.data || []);
    } catch (err: unknown) {
      notifyError(err, 'Failed to load clients for payment creation');
    }
  };

  const loadCountries = async () => {
    try {
      const response = await currencyCountriesApi.getCountries();
      const data = (response.data || []).slice().sort((a, b) => a.name.localeCompare(b.name));
      setCountries(data);
    } catch (err: unknown) {
      notifyError(err, 'Failed to load countries');
    }
  };

  const loadProviders = async () => {
    try {
      const data = await providersApi.getAll();
      setProviders(data || []);
    } catch (err: unknown) {
      notifyError(err, 'Failed to load providers');
    }
  };

  const loadCurrencies = async () => {
    try {
      const response = await adminApi.getCurrencies();
      setCurrencies(response.data || []);
    } catch (err: unknown) {
      notifyError(err, 'Failed to load currencies');
    }
  };

  const resetOutgoingRecipientLookupState = useCallback(() => {
    setRecipientLookupQuery('');
    setRecipientLookupOpen(false);
    setRecipientLookupLoading(false);
    setRecipientLookupResults([]);
  }, []);

  const openEditDraftModal = (payment: Payment) => {
    const normalizedPurpose = (payment.purpose || '').trim();
    const purpose = isPaymentPurposeValue(normalizedPurpose) ? normalizedPurpose : 'Other';

    setOutgoingForm({
      clientId: payment.clientId || '',
      fromAccountId: payment.fromAccountId || '',
      paymentCurrencyId: '',
      amount: String(payment.amount ?? ''),
      paymentDate: payment.paymentDate ? payment.paymentDate.slice(0, 10) : '',
      purpose,
      paymentReference: payment.reference || '',
      paymentKind: payment.paymentKind || 'EXTERNAL',
      recipientClientId: payment.recipientClientId || '',
      beneficiaryName: payment.beneficiary?.name || '',
      beneficiaryBankName: payment.beneficiary?.bankName || '',
      beneficiaryAccountNumber: payment.beneficiary?.accountNumber || payment.beneficiary?.iban || '',
      beneficiarySwift: payment.beneficiary?.swiftCode || '',
      recipientCountryId: payment.beneficiary?.countryId || '',
      bankCountryId: payment.beneficiary?.bankCountryId || '',
      beneficiaryCity: payment.beneficiary?.city || '',
      beneficiaryZipCode: payment.beneficiary?.zipCode || '',
      beneficiaryAddress: payment.beneficiary?.address || '',
    });
    setCreateTab('outgoing');
    setEditingDraftPaymentId(payment.id);
    setCreateModalOpen(true);
    resetOutgoingRecipientLookupState();
  };

  const submitOutgoingCreate = async () => {
    if (!outgoingForm.clientId || !outgoingForm.fromAccountId) {
      notifyErrorMessage('Select client and source account.');
      return;
    }
    if (!outgoingForm.paymentCurrencyId) {
      notifyErrorMessage('Select payment currency.');
      return;
    }
    const selectedSourceAccount = outgoingAccounts.find((a) => a.id === outgoingForm.fromAccountId);
    if (!selectedSourceAccount) {
      notifyErrorMessage('Selected source account was not found.');
      return;
    }
    if (!isAccountActive(selectedSourceAccount)) {
      notifyErrorMessage('Source account must be active.');
      return;
    }
    if (selectedSourceAccount.currencyId !== outgoingForm.paymentCurrencyId) {
      notifyErrorMessage('Payment currency must match source account currency.');
      return;
    }
    const amount = Number(outgoingForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      notifyErrorMessage('Outgoing amount must be greater than 0.');
      return;
    }
    if (!outgoingForm.paymentReference.trim()) {
      notifyErrorMessage('Payment reference is required.');
      return;
    }

    if (outgoingForm.paymentKind === 'INTERNAL') {
      if (!outgoingForm.recipientClientId) {
        notifyErrorMessage('Recipient client is required for internal payment.');
        return;
      }
      if (recipientCompatibility.kind === 'checking') {
        notifyErrorMessage('Recipient compatibility check is in progress. Please wait.');
        return;
      }
      if (recipientCompatibility.kind === 'needs_source') {
        notifyErrorMessage('Select source account to validate recipient compatibility.');
        return;
      }
      if (recipientCompatibility.kind === 'incompatible') {
        notifyErrorMessage(recipientCompatibility.message || 'Recipient account must match source account currency and provider.');
        return;
      }
    } else if (outgoingForm.paymentKind === 'EXTERNAL') {
      if (!outgoingForm.beneficiaryName.trim() || !outgoingForm.beneficiaryBankName.trim() || !outgoingForm.beneficiaryAccountNumber.trim()) {
        notifyErrorMessage('Beneficiary name, bank and account number are required for external payment.');
        return;
      }
      if (!outgoingForm.beneficiarySwift.trim()) {
        notifyErrorMessage('SWIFT code is required for external payment.');
        return;
      }
      if (!outgoingForm.recipientCountryId || !outgoingForm.bankCountryId) {
        notifyErrorMessage('Recipient country and bank country are required for external payment.');
        return;
      }
    }
    // DIGITAL_OUTGOING and DIGITAL_INCOMING don't require additional validation

    try {
      setCreateLoading(true);
      const payload = {
        fromAccountId: outgoingForm.fromAccountId,
        amount,
        paymentDate: outgoingForm.paymentDate || undefined,
        purpose: outgoingForm.purpose,
        paymentReference: outgoingForm.paymentReference.trim(),
        purposeCategory: purposeCategoryForValue(outgoingForm.purpose),
        paymentKind: outgoingForm.paymentKind,
        recipientClientId: outgoingForm.paymentKind === 'INTERNAL' ? outgoingForm.recipientClientId : undefined,
        beneficiaryName: outgoingForm.paymentKind === 'EXTERNAL' ? outgoingForm.beneficiaryName.trim() : undefined,
        beneficiaryBankName: outgoingForm.paymentKind === 'EXTERNAL' ? outgoingForm.beneficiaryBankName.trim() : undefined,
        beneficiaryAccountNumber: outgoingForm.paymentKind === 'EXTERNAL' ? outgoingForm.beneficiaryAccountNumber.trim() : undefined,
        beneficiaryIban: outgoingForm.paymentKind === 'EXTERNAL' ? outgoingForm.beneficiaryAccountNumber.trim() : undefined,
        beneficiarySwift: outgoingForm.paymentKind === 'EXTERNAL' ? outgoingForm.beneficiarySwift.trim().toUpperCase() : undefined,
        countryId: outgoingForm.paymentKind === 'EXTERNAL' ? outgoingForm.recipientCountryId : undefined,
        beneficiaryBankCountryId: outgoingForm.paymentKind === 'EXTERNAL' ? outgoingForm.bankCountryId : undefined,
        beneficiaryCity: outgoingForm.paymentKind === 'EXTERNAL' ? outgoingForm.beneficiaryCity.trim() : undefined,
        beneficiaryZipCode: outgoingForm.paymentKind === 'EXTERNAL' ? outgoingForm.beneficiaryZipCode.trim() : undefined,
        beneficiaryAddress: outgoingForm.paymentKind === 'EXTERNAL' ? outgoingForm.beneficiaryAddress.trim() : undefined,
      };

      if (editingDraftPaymentId) {
        await paymentsApi.updateDraft(outgoingForm.clientId, editingDraftPaymentId, payload);
        notifySuccess('Outgoing payment draft updated.');
      } else {
        await paymentsApi.createDraft(outgoingForm.clientId, payload);
        notifySuccess('Outgoing payment draft created.');
      }

      setOutgoingForm((prev) => ({
        ...prev,
        paymentCurrencyId: '',
        amount: '',
        paymentDate: '',
        purpose: 'Other',
        paymentReference: '',
        recipientClientId: '',
        beneficiaryName: '',
        beneficiaryBankName: '',
        beneficiaryAccountNumber: '',
        beneficiarySwift: '',
        recipientCountryId: '',
        bankCountryId: '',
        beneficiaryCity: '',
        beneficiaryZipCode: '',
        beneficiaryAddress: '',
      }));
      resetOutgoingRecipientLookupState();
      setEditingDraftPaymentId(null);
      setCreateModalOpen(false);
      await fetchPayments();
    } catch (err: unknown) {
      notifyError(err, editingDraftPaymentId ? 'Failed to update outgoing payment draft' : 'Failed to create outgoing payment');
    } finally {
      setCreateLoading(false);
    }
  };

  const buildIncomingBackofficeComment = () => {
    const incomingDetails = [
      incomingForm.beneficiaryName.trim() ? `beneficiaryName=${incomingForm.beneficiaryName.trim()}` : null,
      incomingForm.beneficiaryBankName.trim() ? `beneficiaryBankName=${incomingForm.beneficiaryBankName.trim()}` : null,
      incomingForm.beneficiaryAccountNumber.trim() ? `beneficiaryAccountNumber=${incomingForm.beneficiaryAccountNumber.trim()}` : null,
      incomingForm.beneficiarySwift.trim() ? `beneficiarySwift=${incomingForm.beneficiarySwift.trim().toUpperCase()}` : null,
      incomingForm.recipientCountryId ? `recipientCountryId=${incomingForm.recipientCountryId}` : null,
      incomingForm.bankCountryId ? `bankCountryId=${incomingForm.bankCountryId}` : null,
      incomingForm.beneficiaryCity.trim() ? `beneficiaryCity=${incomingForm.beneficiaryCity.trim()}` : null,
      incomingForm.beneficiaryZipCode.trim() ? `beneficiaryZipCode=${incomingForm.beneficiaryZipCode.trim()}` : null,
      incomingForm.beneficiaryAddress.trim() ? `beneficiaryAddress=${incomingForm.beneficiaryAddress.trim()}` : null,
    ]
      .filter(Boolean)
      .join('; ');
    return [incomingDetails, incomingForm.backofficeComment.trim()].filter(Boolean).join(' | ');
  };

  const resetIncomingFormState = () => {
    setIncomingForm((prev) => ({
      ...prev,
      clientAccountId: incomingMode === 'matched' ? prev.clientAccountId : '',
      providerId: '',
      currencyId: '',
      paymentKind: createTab === 'digital_incoming' ? 'DIGITAL_INCOMING' : 'EXTERNAL',
      amount: '',
      paymentDate: '',
      paymentReference: '',
      beneficiaryName: '',
      beneficiaryBankName: '',
      beneficiaryAccountNumber: '',
      beneficiarySwift: '',
      recipientCountryId: '',
      bankCountryId: '',
      beneficiaryCity: '',
      beneficiaryZipCode: '',
      beneficiaryAddress: '',
      backofficeComment: '',
      paymentMethod: 'SWIFT',
      receivedOnRequisiteId: '',
      methodMismatchReason: '',
    }));
    setEditingIncomingDraftId(null);
    setIncomingFeeQuote(null);
    setIncomingOverrideFeeAmount('');
    setIncomingOverrideFeeReason('');
  };

  const buildIncomingDraftPayload = (): SaveIncomingDraftRequest | null => {
    const amount = Number(incomingForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      notifyErrorMessage('Incoming amount must be greater than 0.');
      return null;
    }
    const mergedBackofficeComment = buildIncomingBackofficeComment();

    if (incomingMode === 'matched') {
      if (!incomingForm.clientId || !incomingForm.clientAccountId) {
        notifyErrorMessage('Select client and destination account.');
        return null;
      }
      const selectedAccount = incomingAccounts.find((a) => a.id === incomingForm.clientAccountId);
      if (!selectedAccount) {
        notifyErrorMessage('Destination account not found in selected client accounts.');
        return null;
      }
      if (!isAccountActive(selectedAccount)) {
        notifyErrorMessage('Destination account must be active.');
        return null;
      }
      return {
        clientId: incomingForm.clientId,
        clientAccountId: incomingForm.clientAccountId,
        amount,
        paymentDate: incomingForm.paymentDate || undefined,
        providerReference: incomingForm.paymentReference.trim() || undefined,
        paymentKind: incomingForm.paymentKind,
        paymentMethod: incomingForm.paymentMethod,
        receivedOnRequisiteId: incomingForm.receivedOnRequisiteId || undefined,
        reason: incomingForm.methodMismatchReason.trim() || undefined,
        backofficeComment: mergedBackofficeComment || undefined,
        clientUnresolved: false,
      };
    }

    if (!incomingForm.providerId || !incomingForm.currencyId) {
      notifyErrorMessage('Select provider and currency for unmatched incoming.');
      return null;
    }
    return {
      providerId: incomingForm.providerId,
      currencyId: incomingForm.currencyId,
      amount,
      paymentDate: incomingForm.paymentDate || undefined,
      providerReference: incomingForm.paymentReference.trim() || undefined,
      paymentKind: incomingForm.paymentKind,
      backofficeComment: mergedBackofficeComment || undefined,
      clientUnresolved: true,
    };
  };

  const buildOftIncomingDraftPayload = (): SaveOftIncomingDraftRequest | null => {
    const amount = Number(incomingForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      notifyErrorMessage('Incoming amount must be greater than 0.');
      return null;
    }
    if (!incomingForm.clientId || !incomingForm.clientAccountId) {
      notifyErrorMessage('Select client and destination account.');
      return null;
    }
    const selectedAccount = incomingAccounts.find((a) => a.id === incomingForm.clientAccountId);
    if (!selectedAccount) {
      notifyErrorMessage('Destination account not found in selected client accounts.');
      return null;
    }
    if (!isAccountActive(selectedAccount)) {
      notifyErrorMessage('Destination account must be active.');
      return null;
    }
    return {
      clientId: incomingForm.clientId,
      clientAccountId: incomingForm.clientAccountId,
      amount,
      paymentDate: incomingForm.paymentDate || undefined,
      providerReference: incomingForm.paymentReference.trim() || undefined,
      backofficeComment: incomingForm.backofficeComment.trim() || undefined,
    };
  };

  const persistIncomingDraft = async (): Promise<string | null> => {
    const isOft = createTab === 'digital_incoming';
    const payload = isOft ? buildOftIncomingDraftPayload() : buildIncomingDraftPayload();
    if (!payload) return null;

    const api = isOft ? oftIncomingApi : incomingPaymentsApi;
    if (editingIncomingDraftId) {
      await api.updateDraft(editingIncomingDraftId, payload);
      return editingIncomingDraftId;
    }
    const response = await api.createDraft(payload);
    return response.data.id;
  };

  const saveIncomingDraft = async () => {
    try {
      setCreateLoading(true);
      const draftId = await persistIncomingDraft();
      if (!draftId) return;
      notifySuccess(editingIncomingDraftId ? 'Incoming draft updated.' : 'Incoming draft saved.');
      resetIncomingFormState();
      setCreateModalOpen(false);
      await refreshIncomingLists();
    } catch (err: unknown) {
      notifyError(err, editingIncomingDraftId ? 'Failed to update incoming draft' : 'Failed to save incoming draft');
    } finally {
      setCreateLoading(false);
    }
  };

  const submitIncomingDraft = async () => {
    try {
      setCreateLoading(true);
      const draftId = await persistIncomingDraft();
      if (!draftId) return;
      const api = createTab === 'digital_incoming' ? oftIncomingApi : incomingPaymentsApi;
      await api.submitDraft(draftId);
      notifySuccess('Incoming payment submitted.');
      resetIncomingFormState();
      setCreateModalOpen(false);
      await refreshIncomingLists();
    } catch (err: unknown) {
      notifyError(err, 'Failed to submit incoming payment');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleIncomingOverrideFee = async () => {
    if (!editingIncomingDraftId) return;
    const newFeeAmount = Number(incomingOverrideFeeAmount);
    if (!Number.isFinite(newFeeAmount) || newFeeAmount < 0) {
      notifyErrorMessage('Fee amount must be zero or greater.');
      return;
    }
    if (!incomingOverrideFeeReason.trim()) {
      notifyErrorMessage('Override reason is required.');
      return;
    }
    try {
      setCreateLoading(true);
      const api = createTab === 'digital_incoming' ? oftIncomingApi : incomingPaymentsApi;
      const response = await api.overrideFee(editingIncomingDraftId, {
        newFeeAmount,
        reason: incomingOverrideFeeReason.trim(),
      });
      setIncomingOverrideFeeAmount(String(response.data.feeAmount ?? newFeeAmount));
      setIncomingOverrideFeeReason('');
      notifySuccess('Draft fee overridden.');
      const amount = Number(incomingForm.amount);
      if (Number.isFinite(amount) && amount > 0) {
        setIncomingFeeQuote({
          grossAmount: amount,
          feeAmount: response.data.feeAmount,
          netAmount: Math.max(amount - response.data.feeAmount, 0),
          currencyId: response.data.currencyId,
        });
      }
    } catch (err: unknown) {
      notifyError(err, 'Failed to override draft fee');
    } finally {
      setCreateLoading(false);
    }
  };

  const openEditIncomingDraftModal = (payment: IncomingPayment | OftIncoming, tab: 'incoming' | 'digital_incoming') => {
    const hasClient = Boolean(payment.clientId && payment.clientId !== '00000000-0000-0000-0000-000000000000');
    setIncomingForm({
      clientId: hasClient ? payment.clientId! : '',
      clientAccountId: payment.clientAccountId || '',
      providerId: payment.providerId || '',
      currencyId: payment.currencyId || '',
      paymentKind: tab === 'digital_incoming' ? 'DIGITAL_INCOMING' : ((payment.paymentKind || 'EXTERNAL') as 'EXTERNAL' | 'INTERNAL' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING'),
      amount: String(payment.amount ?? ''),
      paymentDate: payment.paymentDate ? payment.paymentDate.slice(0, 10) : '',
      paymentReference: payment.providerReference || '',
      beneficiaryName: 'beneficiaryName' in payment ? (payment.beneficiaryName || '') : '',
      beneficiaryBankName: 'beneficiaryBankName' in payment ? (payment.beneficiaryBankName || '') : '',
      beneficiaryAccountNumber: 'beneficiaryAccountNumber' in payment ? (payment.beneficiaryAccountNumber || '') : '',
      beneficiarySwift: 'beneficiarySwift' in payment ? (payment.beneficiarySwift || '') : '',
      recipientCountryId: 'recipientCountryId' in payment ? (payment.recipientCountryId || '') : '',
      bankCountryId: 'bankCountryId' in payment ? (payment.bankCountryId || '') : '',
      beneficiaryCity: 'beneficiaryCity' in payment ? (payment.beneficiaryCity || '') : '',
      beneficiaryZipCode: 'beneficiaryZipCode' in payment ? (payment.beneficiaryZipCode || '') : '',
      beneficiaryAddress: 'beneficiaryAddress' in payment ? (payment.beneficiaryAddress || '') : '',
      backofficeComment: payment.backofficeComment || '',
      paymentMethod: ('paymentMethod' in payment && payment.paymentMethod === 'SEPA' ? 'SEPA' : 'SWIFT'),
      receivedOnRequisiteId: 'receivedOnRequisiteId' in payment ? (payment.receivedOnRequisiteId || '') : '',
      methodMismatchReason: '',
    });
    setIncomingMode(hasClient ? 'matched' : 'unmatched');
    setCreateTab(tab);
    setEditingIncomingDraftId(payment.id);
    setIncomingOverrideFeeAmount(String(payment.feeAmount ?? ''));
    setIncomingOverrideFeeReason('');
    setIncomingFeeQuote(null);
    setCreateModalOpen(true);
  };

  const handleIncomingSubmitDraftFromList = async (payment: IncomingPayment | OftIncoming) => {
    try {
      setActionLoading(payment.id);
      const api = paymentFlowTab === 'digital_incoming' ? oftIncomingApi : incomingPaymentsApi;
      await api.submitDraft(payment.id);
      notifySuccess('Draft submitted.');
      await refreshIncomingLists();
    } catch (err: unknown) {
      notifyError(err, 'Failed to submit draft');
    } finally {
      setActionLoading(null);
    }
  };

  const handleIncomingDeleteDraft = async (payment: IncomingPayment | OftIncoming) => {
    const confirmed = window.confirm('Delete this incoming draft? This action cannot be undone.');
    if (!confirmed) return;
    try {
      setActionLoading(payment.id);
      const api = paymentFlowTab === 'digital_incoming' ? oftIncomingApi : incomingPaymentsApi;
      await api.deleteDraft(payment.id);
      notifySuccess('Draft deleted.');
      await refreshIncomingLists();
    } catch (err: unknown) {
      notifyError(err, 'Failed to delete draft');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      setActionLoading(id);
      await paymentsApi.approve(id);
      await fetchPayments();
    } catch (err: unknown) {
      notifyError(err, 'Failed to approve payment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerifyIban = async (id: string) => {
    try {
      setActionLoading(id);
      await paymentsApi.verifyIban(id);
      notifySuccess('IBAN verified');
      await fetchPayments();
    } catch (err: unknown) {
      notifyError(err, 'Failed to verify IBAN');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    try {
      setActionLoading(rejectModal.id);
      await paymentsApi.reject(rejectModal.id, { reason: rejectReason, comment: rejectComment });
      setRejectModal({ id: '', open: false });
      setRejectReason('');
      setRejectComment('');
      await fetchPayments();
    } catch (err: unknown) {
      notifyError(err, 'Failed to reject payment');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePutOnHold = async () => {
    if (!holdReason.trim()) return;
    try {
      setActionLoading(holdModal.id);
      await paymentsApi.putOnHold(holdModal.id, { reason: holdReason, comment: holdComment });
      setHoldModal({ id: '', open: false });
      setHoldReason('');
      setHoldComment('');
      await fetchPayments();
    } catch (err: unknown) {
      notifyError(err, 'Failed to put payment on hold');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSend = async (id: string) => {
    try {
      setActionLoading(id);
      await paymentsApi.send(id);
      await fetchPayments();
    } catch (err: unknown) {
      notifyError(err, 'Failed to send payment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendInternal = async (id: string) => {
    try {
      setActionLoading(id);
      await paymentsApi.sendInternal(id);
      await fetchPayments();
    } catch (err: unknown) {
      notifyError(err, 'Failed to send internal payment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReleaseFromHold = async (id: string) => {
    try {
      setActionLoading(id);
      await paymentsApi.releaseFromHold(id);
      await fetchPayments();
    } catch (err: unknown) {
      notifyError(err, 'Failed to release payment from hold');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartUnderReview = async (id: string) => {
    try {
      setActionLoading(id);
      await paymentsApi.startUnderReview(id);
      await fetchPayments();
    } catch (err: unknown) {
      notifyError(err, 'Failed to move payment under review');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelPayment = async () => {
    try {
      setActionLoading(cancelModal.id);
      await paymentsApi.cancel(cancelModal.id, { reason: cancelReason.trim() || undefined });
      setCancelModal({ id: '', open: false });
      setCancelReason('');
      await fetchPayments();
    } catch (err: unknown) {
      notifyError(err, 'Failed to cancel payment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmitDraft = async (payment: Payment) => {
    try {
      setActionLoading(payment.id);
      await paymentsApi.submitDraft(payment.clientId, payment.id);
      notifySuccess('Draft submitted for review.');
      await fetchPayments();
    } catch (err: unknown) {
      notifyError(err, 'Failed to submit draft');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteDraft = async (payment: Payment) => {
    const confirmed = window.confirm('Delete this draft? This action cannot be undone.');
    if (!confirmed) return;
    try {
      setActionLoading(payment.id);
      await paymentsApi.deleteDraft(payment.id);
      notifySuccess('Draft deleted.');
      await fetchPayments();
    } catch (err: unknown) {
      notifyError(err, 'Failed to delete draft');
    } finally {
      setActionLoading(null);
    }
  };

  const handleIncomingUnderReview = async (id: string) => {
    try {
      setActionLoading(id);
      const api = paymentFlowTab === 'digital_incoming' ? oftIncomingApi : incomingPaymentsApi;
      await api.underReview(id);
      await refreshIncomingLists();
    } catch (err: unknown) {
      notifyError(err, 'Failed to move incoming payment under review');
    } finally {
      setActionLoading(null);
    }
  };

  const handleIncomingApprove = async (id: string) => {
    try {
      setActionLoading(id);
      const api = paymentFlowTab === 'digital_incoming' ? oftIncomingApi : incomingPaymentsApi;
      await api.approve(id);
      notifySuccess('Incoming payment approved for crediting.');
      await refreshIncomingLists();
    } catch (err: unknown) {
      notifyError(err, 'Failed to approve incoming payment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleIncomingRelease = async (id: string) => {
    try {
      setActionLoading(id);
      const api = paymentFlowTab === 'digital_incoming' ? oftIncomingApi : incomingPaymentsApi;
      await api.releaseToAvailable(id);
      notifySuccess('Funds released to client account.');
      await refreshIncomingLists();
    } catch (err: unknown) {
      notifyError(err, 'Failed to release incoming payment to available');
    } finally {
      setActionLoading(null);
    }
  };

  const handleIncomingReject = async () => {
    if (!incomingRejectReason.trim()) return;
    try {
      setActionLoading(incomingRejectModal.id);
      const api = paymentFlowTab === 'digital_incoming' ? oftIncomingApi : incomingPaymentsApi;
      await api.reject(incomingRejectModal.id, { reason: incomingRejectReason.trim() });
      setIncomingRejectModal({ id: '', open: false });
      setIncomingRejectReason('');
      await refreshIncomingLists();
    } catch (err: unknown) {
      notifyError(err, 'Failed to reject incoming payment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenLinksModal = async (id: string) => {
    setLinksModal({ id, open: true });
    setLinksLoading(true);
    try {
      const response = await paymentsApi.getLedgerLinks(id);
      setLedgerLinks(response.data.links || []);
    } catch (err: unknown) {
      console.error('Failed to load ledger links:', err);
      notifyError(err, 'Failed to load ledger links');
      setLedgerLinks([]);
    } finally {
      setLinksLoading(false);
    }
  };

  const handleDownloadConfirmation = async (id: string) => {
    try {
      setActionLoading(id);
      const response = await paymentsApi.downloadConfirmation(id);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = confirmationFileName(response.headers['content-disposition'], id);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      notifyError(err, 'Failed to download payment confirmation');
    } finally {
      setActionLoading(null);
    }
  };

  const scopeMatchesClient = (p: { clientId?: string | null }) =>
    !selectedClient || p.clientId === selectedClient.id;

  const draftCount = payments.filter(
    (p) => scopeMatchesClient(p) && p.status?.toUpperCase() === 'DRAFT'
  ).length;

  const isOftOutgoingView = paymentFlowTab === 'digital_outgoing';
  const isOftIncomingView = paymentFlowTab === 'digital_incoming';

  const filteredPayments = payments.filter((p) => {
    if (!scopeMatchesClient(p)) return false;
    const st = p.status?.toUpperCase() || '';
    if (listTab === 'drafts') {
      if (st !== 'DRAFT') return false;
    } else {
      if (st === 'DRAFT') return false;
      if (filterStatus !== 'all' && st !== filterStatus.toUpperCase()) return false;
    }
    if (!isOftOutgoingView && filterKind !== 'all' && p.paymentKind !== filterKind) return false;
    if (filterMethod !== 'all' && (p.paymentMethod || 'SWIFT').toUpperCase() !== filterMethod) return false;
    // Filter out digital payments from regular outgoing tab
    if (paymentFlowTab === 'outgoing' && (p.paymentKind === 'DIGITAL_OUTGOING' || p.paymentKind === 'DIGITAL_INCOMING')) return false;
    // Show only digital outgoing in digital_outgoing tab
    if (paymentFlowTab === 'digital_outgoing' && p.paymentKind !== 'DIGITAL_OUTGOING') return false;
    return true;
  });

  const incomingPaymentsForTab = useMemo((): IncomingPayment[] => {
    if (isOftIncomingView) {
      return oftIncomingPayments.map((p) => ({
        ...p,
        clientId: p.clientId && p.clientId !== '00000000-0000-0000-0000-000000000000' ? p.clientId : null,
        clientAccountId: p.clientAccountId && p.clientAccountId !== '00000000-0000-0000-0000-000000000000' ? p.clientAccountId : null,
      }));
    }
    return incomingPayments.filter((p) => (p.paymentKind || 'EXTERNAL').toUpperCase() !== 'DIGITAL_INCOMING');
  }, [incomingPayments, oftIncomingPayments, isOftIncomingView]);

  const filteredIncomingPayments = incomingPaymentsForTab.filter((p) => {
    if (selectedClient?.id && p.clientId !== selectedClient.id) return false;
    const status = (p.status || '').toUpperCase();
    if (incomingFilterStatus !== 'all' && status !== incomingFilterStatus.toUpperCase()) return false;
    const kind = (p.paymentKind || 'EXTERNAL').toUpperCase();
    if (!isOftIncomingView && incomingFilterKind !== 'all' && kind !== incomingFilterKind.toUpperCase()) return false;
    if (incomingFilterMethod !== 'all' && (p.paymentMethod || 'SWIFT').toUpperCase() !== incomingFilterMethod) return false;
    return true;
  });

  const providerNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of providers) {
      if (!p.id) continue;
      map.set(p.id, p.name);
    }
    return map;
  }, [providers]);
  const clientById = useMemo(() => {
    const map = new Map<string, Client>();
    for (const client of clients) {
      if (!client?.id) continue;
      map.set(client.id, client);
    }
    return map;
  }, [clients]);

  const selectedRecipientClient = outgoingForm.recipientClientId
    ? (clientById.get(outgoingForm.recipientClientId) ?? recipientLookupResults.find((client) => client.id === outgoingForm.recipientClientId) ?? null)
    : null;
  const selectedRecipientAccounts = useMemo(
    () => (outgoingForm.recipientClientId ? (recipientAccountsByClientId[outgoingForm.recipientClientId] ?? []) : []),
    [outgoingForm.recipientClientId, recipientAccountsByClientId]
  );
  const recipientCompatibility = useMemo<InternalRecipientCompatibility>(() => {
    if (outgoingForm.paymentKind !== 'INTERNAL') {
      return { kind: 'idle', message: '' };
    }
    if (!outgoingForm.recipientClientId) {
      return { kind: 'idle', message: 'Select recipient client.' };
    }
    if (!selectedOutgoingSourceAccount || !outgoingForm.paymentCurrencyId) {
      return { kind: 'needs_source', message: 'Select source account to validate recipient compatibility.' };
    }
    if (recipientAccountLoadingByClientId[outgoingForm.recipientClientId]) {
      return { kind: 'checking', message: 'Checking recipient accounts...' };
    }

    const matchedAccount = selectedRecipientAccounts.find(
      (account) =>
        account.providerId === selectedOutgoingSourceAccount.providerId &&
        account.currencyId === selectedOutgoingSourceAccount.currencyId
    );
    if (matchedAccount) {
      const providerName = providerNameById.get(matchedAccount.providerId) || matchedAccount.providerId;
      const currency = resolveCurrencyLabel(matchedAccount.currencyId) || matchedAccount.currencyId;
      return {
        kind: 'compatible',
        message: `Compatible: ${currency} · ${providerName}${matchedAccount.accountNumber ? ` · ${matchedAccount.accountNumber}` : ''}`,
      };
    }

    const sourceProviderName = providerNameById.get(selectedOutgoingSourceAccount.providerId) || selectedOutgoingSourceAccount.providerId;
    const sourceCurrency = resolveCurrencyLabel(selectedOutgoingSourceAccount.currencyId) || selectedOutgoingSourceAccount.currencyId;
    return {
      kind: 'incompatible',
      message: `No matching recipient account for ${sourceCurrency} · ${sourceProviderName}.`,
    };
  }, [
    outgoingForm.paymentCurrencyId,
    outgoingForm.paymentKind,
    outgoingForm.recipientClientId,
    providerNameById,
    recipientAccountLoadingByClientId,
    resolveCurrencyLabel,
    selectedOutgoingSourceAccount,
    selectedRecipientAccounts,
  ]);
  const hasRecipientCompatibilityIssue =
    outgoingForm.paymentKind === 'INTERNAL' &&
    !!outgoingForm.recipientClientId &&
    (recipientCompatibility.kind === 'incompatible' || recipientCompatibility.kind === 'needs_source' || recipientCompatibility.kind === 'checking');

  const getOutgoingTypeBadge = (payment: Payment) => {
    const kind = (payment.paymentKind || 'EXTERNAL').toUpperCase();
    if (kind === 'DIGITAL_OUTGOING') return { label: 'OFT', className: 'bg-cyan-100 text-cyan-800' };
    if (kind === 'INTERNAL') return { label: 'Internal', className: 'bg-purple-100 text-purple-700' };
    return { label: 'External', className: 'bg-slate-100 text-slate-700' };
  };

  const getIncomingTypeBadge = (payment: IncomingPayment) => {
    const kind = (payment.paymentKind || 'EXTERNAL').toUpperCase();
    if (kind === 'DIGITAL_INCOMING') return { label: 'OFT', className: 'bg-cyan-100 text-cyan-800' };
    if (kind === 'INTERNAL') return { label: 'Internal', className: 'bg-purple-100 text-purple-700' };
    return { label: 'External', className: 'bg-slate-100 text-slate-700' };
  };

  const formatAmount = (amount: number, currency: string) => {
    try {
      return amount.toLocaleString('en-US', { style: 'currency', currency, minimumFractionDigits: 2 });
    } catch {
      return `${amount.toFixed(2)} ${currency}`;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Payments</h1>
          <p className="text-gray-600">Track all payment transactions</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Payments</h1>
          <p className="text-gray-600">Track all payment transactions</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-red-600">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Payments</h1>
        <p className="text-gray-600">Track outgoing and incoming payment transactions</p>
      </div>

      <div className="mb-6 flex justify-end">
        <button
          type="button"
          onClick={() => {
            setEditingDraftPaymentId(null);
            setCreateModalOpen(true);
            resetOutgoingRecipientLookupState();
          }}
          className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
        >
          Create payment
        </button>
      </div>

      {selectedClient && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-lg">
          <UserCheck className="h-4 w-4 text-indigo-600 flex-shrink-0" />
          <p className="text-sm text-indigo-800 flex-1">
            Showing payments for: <span className="font-semibold">{getClientDisplayName(selectedClient) || selectedClient.fullName}</span>
            {selectedClient.publicId && <span className="ml-2 font-mono text-indigo-600">{formatPublicIdDisplay(selectedClient.publicId)}</span>}
          </p>
          <button
            onClick={clearSelectedClient}
            className="text-indigo-400 hover:text-indigo-600 transition-colors"
            title="Show all payments"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="mb-4 border-b border-gray-200">
        <nav className="flex gap-1 -mb-px" aria-label="Payment directions">
          <button
            type="button"
            onClick={() => setPaymentFlowTab('outgoing')}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border border-b-0 transition-colors ${
              paymentFlowTab === 'outgoing'
                ? 'bg-white border-gray-200 text-gray-900 z-10'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Outgoing
          </button>
          <button
            type="button"
            onClick={() => setPaymentFlowTab('incoming')}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border border-b-0 transition-colors ${
              paymentFlowTab === 'incoming'
                ? 'bg-white border-gray-200 text-gray-900 z-10'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Incoming
          </button>
          <button
            type="button"
            onClick={() => setPaymentFlowTab('digital_outgoing')}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border border-b-0 transition-colors ${
              paymentFlowTab === 'digital_outgoing'
                ? 'bg-white border-gray-200 text-gray-900 z-10'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            OFT Outgoing
          </button>
          <button
            type="button"
            onClick={() => setPaymentFlowTab('digital_incoming')}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border border-b-0 transition-colors ${
              paymentFlowTab === 'digital_incoming'
                ? 'bg-white border-gray-200 text-gray-900 z-10'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            OFT Incoming
          </button>
          <button
            type="button"
            onClick={() => setPaymentFlowTab('returns')}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border border-b-0 transition-colors ${
              paymentFlowTab === 'returns'
                ? 'bg-white border-gray-200 text-gray-900 z-10'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Returns
          </button>
        </nav>
      </div>

      {paymentFlowTab === 'returns' ? (
        <PaymentReturnsPanel
          selectedClientId={selectedClient?.id}
          canManage={canOperationsOutgoingActions(adminRole)}
          canApprove={canApprovePaymentReturns(adminRole)}
          initialOriginalPaymentId={returnOriginalPaymentId}
          onInitialOriginalPaymentConsumed={() => setReturnOriginalPaymentId(undefined)}
        />
      ) : (paymentFlowTab === 'outgoing' || isOftOutgoingView) ? (
        <>
          {/* Processing vs drafts */}
          <div className="mb-4 border-b border-gray-200">
            <nav className="flex gap-1 -mb-px" aria-label="Outgoing payment lists">
              <button
                type="button"
                onClick={() => setListTab('processing')}
                className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border border-b-0 transition-colors ${
                  listTab === 'processing'
                    ? 'bg-white border-gray-200 text-gray-900 z-10'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Processing
              </button>
              <button
                type="button"
                onClick={() => setListTab('drafts')}
                className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border border-b-0 transition-colors inline-flex items-center gap-2 ${
                  listTab === 'drafts'
                    ? 'bg-white border-gray-200 text-gray-900 z-10'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Drafts
                {draftCount > 0 ? (
                  <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-800">
                    {draftCount}
                  </span>
                ) : null}
              </button>
            </nav>
          </div>

          <div className="mb-4 flex flex-wrap gap-4">
            {listTab === 'processing' ? (
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="UNDER_REVIEW">Under Review</option>
                <option value="APPROVED">Approved</option>
                <option value="ON_HOLD">On Hold</option>
                <option value="PENDING">Pending</option>
                <option value="PROCESSING">Processing</option>
                <option value="SENT">Sent</option>
                <option value="COMPLETED">Completed</option>
                <option value="FAILED">Failed</option>
                <option value="REJECTED">Rejected</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            ) : (
              <p className="text-sm text-gray-600 self-center">
                Client drafts only — not yet submitted for approval.
              </p>
            )}
            {!isOftOutgoingView && (
            <select
              value={filterKind}
              onChange={(e) => setFilterKind(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="EXTERNAL">External</option>
              <option value="INTERNAL">Internal</option>
            </select>
            )}
            <select
              value={filterMethod}
              onChange={(e) => setFilterMethod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Methods</option>
              <option value="SWIFT">SWIFT</option>
              <option value="SEPA">SEPA</option>
              <option value="INTERNAL">Internal</option>
            </select>
            <span className="text-sm text-gray-500 self-center">
              {filteredPayments.length} payment{filteredPayments.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client / source account</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                        {listTab === 'drafts' ? 'No draft payments' : 'No payments found'}
                      </td>
                    </tr>
                  ) : (
                    filteredPayments.map((payment) => {
                  const statusKey = payment.status?.toUpperCase() || '';
                  const typeBadge = getOutgoingTypeBadge(payment);
                  const isInternal = payment.paymentKind === 'INTERNAL';
                  const isDraft = statusKey === 'DRAFT';
                  const isApproved = statusKey === 'APPROVED';
                  const isSubmitted = statusKey === 'SUBMITTED' || statusKey === 'UNDER_REVIEW';
                  const isExactlySubmitted = statusKey === 'SUBMITTED';
                  const isOnHold   = statusKey === 'ON_HOLD';
                  const canCancel  = isSubmitted || isApproved || isOnHold;
                  const canDownloadConfirmation = payment.paymentKind?.toUpperCase() === 'EXTERNAL' && ['SENT', 'COMPLETED'].includes(statusKey);
                  const isLoading  = actionLoading === payment.id;

                  return (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {new Date(payment.paymentDate || payment.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${typeBadge.className}`}>
                          {typeBadge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${
                          payment.paymentMethod === 'SEPA'
                            ? 'bg-blue-100 text-blue-800'
                            : payment.paymentMethod === 'INTERNAL'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-gray-100 text-gray-700'
                        }`}>
                          {payment.paymentMethod || (isInternal ? 'INTERNAL' : 'SWIFT')}
                        </span>
                        {payment.paymentMethod === 'SEPA' && (
                          <span className={`ml-1 inline-block rounded px-2 py-0.5 text-xs font-medium ${
                            payment.ibanVerified ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {payment.ibanVerified ? 'IBAN verified' : 'IBAN pending'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 min-w-[10rem] max-w-[16rem]">
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate" title={payment.clientDisplayName || payment.clientEmail || undefined}>
                            {payment.clientDisplayName || payment.clientEmail || '—'}
                          </p>
                          {(payment.clientPublicId || (payment.clientDisplayName && payment.clientEmail)) ? (
                            <p className="text-xs text-gray-500 truncate">
                              {[
                                formatPublicIdDisplay(payment.clientPublicId) || null,
                                payment.clientDisplayName && payment.clientEmail ? payment.clientEmail : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                          ) : null}
                          <p className="text-xs text-gray-600 mt-0.5 truncate" title={[payment.fromAccountCurrency, payment.fromAccountNumber].filter(Boolean).join(' · ')}>
                            {[payment.fromAccountCurrency, payment.fromAccountNumber].filter(Boolean).join(' · ') || '—'}
                            {payment.clientAccountBankName ? (
                              <span className="text-gray-400"> · {payment.clientAccountBankName}</span>
                            ) : null}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {isInternal ? (
                          <div>
                            <p className="text-sm font-medium text-gray-900">{payment.recipientClientEmail || 'Internal'}</p>
                            <p className="text-xs text-gray-500">{formatPublicIdDisplay(payment.recipientClientPublicId)}</p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm font-medium text-gray-900">{payment.beneficiary?.name || '-'}</p>
                            <p className="text-xs text-gray-500">{payment.beneficiary?.accountNumber || ''}</p>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{payment.currency}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                        {formatAmount(payment.amount, payment.currency)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[statusKey] || 'bg-gray-100 text-gray-700'}`}>
                          {payment.status}
                        </span>
                        {payment.providerStatus && (
                          <p className="mt-1 text-xs font-medium text-indigo-700">
                            {payment.providerName || 'Provider'}: {payment.providerStatus}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-center flex-wrap">
                          <button
                            onClick={() => setDetailPaymentId(payment.id)}
                            disabled={isLoading}
                            className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
                            title="View full payment details"
                          >
                            <PanelRightOpen className="h-3 w-3" />
                            Details
                          </button>
                          {canDownloadConfirmation && (
                            <button
                              onClick={() => void handleDownloadConfirmation(payment.id)}
                              disabled={isLoading}
                              className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-50"
                            >
                              {isLoading ? '...' : 'Download confirmation'}
                            </button>
                          )}
                          {isDraft && (
                            <>
                              <button
                                onClick={() => void handleSubmitDraft(payment)}
                                disabled={isLoading}
                                className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                                title="Submit draft for compliance review and approval flow"
                              >
                                {isLoading ? '...' : 'Submit'}
                              </button>
                              <button
                                onClick={() => openEditDraftModal(payment)}
                                disabled={isLoading}
                                className="px-2 py-1 bg-amber-500 text-white text-xs rounded hover:bg-amber-600 disabled:opacity-50"
                                title="Edit draft payment"
                              >
                                Edit
                              </button>
                              {canDeleteOutgoingDraft(adminRole, adminUserId, payment.createdByUserId) && (
                                <button
                                  onClick={() => void handleDeleteDraft(payment)}
                                  disabled={isLoading}
                                  className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
                                  title="Delete draft payment"
                                >
                                  Delete draft
                                </button>
                              )}
                            </>
                          )}
                          {isExactlySubmitted && (
                            <button
                              onClick={() => handleStartUnderReview(payment.id)}
                              disabled={isLoading}
                              className="px-2 py-1 bg-amber-500 text-white text-xs rounded hover:bg-amber-600 disabled:opacity-50"
                              title="Take payment for review. After this the client can no longer cancel it."
                            >
                              {isLoading ? '...' : 'In Review'}
                            </button>
                          )}
                          {payment.paymentMethod === 'SEPA' && !payment.ibanVerified && (
                            <button
                              onClick={() => void handleVerifyIban(payment.id)}
                              disabled={isLoading}
                              className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              {isLoading ? '...' : 'Verify IBAN'}
                            </button>
                          )}
                          {isSubmitted && (
                            <>
                              <button
                                onClick={() => handleApprove(payment.id)}
                                disabled={isLoading}
                                className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                {isLoading ? '...' : 'Approve'}
                              </button>
                              <button
                                onClick={() => setHoldModal({ id: payment.id, open: true })}
                                disabled={isLoading}
                                className="px-2 py-1 bg-orange-500 text-white text-xs rounded hover:bg-orange-600 disabled:opacity-50"
                              >
                                Hold
                              </button>
                              <button
                                onClick={() => setRejectModal({ id: payment.id, open: true })}
                                disabled={isLoading}
                                className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {isApproved && (
                            <>
                              {isInternal ? (
                                <button
                                  onClick={() => handleSendInternal(payment.id)}
                                  disabled={isLoading}
                                  className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 disabled:opacity-50"
                                >
                                  {isLoading ? '...' : 'Send Internal'}
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleSend(payment.id)}
                                  disabled={isLoading}
                                  className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                                >
                                  {isLoading ? '...' : 'Send'}
                                </button>
                              )}
                              <button
                                onClick={() => setRejectModal({ id: payment.id, open: true })}
                                disabled={isLoading}
                                className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {isOnHold && (
                            <>
                              <button
                                onClick={() => handleReleaseFromHold(payment.id)}
                                disabled={isLoading}
                                className="px-2 py-1 bg-teal-600 text-white text-xs rounded hover:bg-teal-700 disabled:opacity-50"
                              >
                                {isLoading ? '...' : 'Release'}
                              </button>
                              <button
                                onClick={() => setRejectModal({ id: payment.id, open: true })}
                                disabled={isLoading}
                                className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {canCancel && (
                            <button
                              onClick={() => { setCancelReason(''); setCancelModal({ id: payment.id, open: true }); }}
                              disabled={isLoading}
                              className="px-2 py-1 bg-rose-600 text-white text-xs rounded hover:bg-rose-700 disabled:opacity-50"
                              title="Cancel payment and return principal + fee to the client"
                            >
                              Cancel payment
                            </button>
                          )}
                          {payment.rejectionReason && (
                            <span className="text-xs text-red-500 self-center" title={payment.rejectionReason}>
                              ⚠ {payment.rejectionReason.slice(0, 30)}
                            </span>
                          )}
                          <button
                            onClick={() => handleOpenLinksModal(payment.id)}
                            disabled={isLoading}
                            className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 disabled:opacity-50 flex items-center gap-1"
                            title="View Ledger Transaction Links (Audit Trail)"
                          >
                            <Link2 className="h-3 w-3" />
                            Links
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (paymentFlowTab === 'incoming' || isOftIncomingView) ? (
        <>
          <div className="mb-4 flex flex-wrap gap-4">
            {!isOftIncomingView && (
            <select
              value={incomingFilterKind}
              onChange={(e) => setIncomingFilterKind(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="EXTERNAL">External</option>
              <option value="INTERNAL">Internal</option>
            </select>
            )}
            <select
              value={incomingFilterMethod}
              onChange={(e) => setIncomingFilterMethod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Methods</option>
              <option value="SWIFT">SWIFT</option>
              <option value="SEPA">SEPA</option>
              <option value="INTERNAL">Internal</option>
            </select>
            <select
              value={incomingFilterStatus}
              onChange={(e) => setIncomingFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="DRAFT">Draft</option>
              <option value="PENDING">Pending</option>
              <option value="UNDER_REVIEW">Under Review</option>
              <option value="PENDING_CREDITING">Pending crediting</option>
              <option value="CREDITED">Credited</option>
              <option value="REJECTED">Rejected</option>
            </select>
            <span className="text-sm text-gray-500 self-center">
              {filteredIncomingPayments.length} payment{filteredIncomingPayments.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Received on IBAN</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client / account</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredIncomingPayments.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                        No incoming payments found
                      </td>
                    </tr>
                  ) : (
                    filteredIncomingPayments.map((payment) => {
                      const statusKey = (payment.status || '').toUpperCase();
                      const isDraft = statusKey === 'DRAFT';
                      const isPending = statusKey === 'PENDING';
                      const isUnderReview = statusKey === 'UNDER_REVIEW';
                      const isPendingCrediting = statusKey === 'PENDING_CREDITING';
                      const canReview = isPending && canComplianceIncomingActions(adminRole);
                      const canApprove = isUnderReview && canComplianceIncomingActions(adminRole);
                      const canReleaseFunds = isPendingCrediting && canOperationsIncomingActions(adminRole);
                      const canReject = (isPending || isUnderReview || isPendingCrediting) && canRejectIncomingPayment(adminRole);
                      const isLoading = actionLoading === payment.id;
                      const providerName = providerNameById.get(payment.providerId) || payment.providerId;
                      const typeBadge = getIncomingTypeBadge(payment);

                      return (
                        <tr key={payment.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {new Date(payment.paymentDate || payment.createdAt || payment.registeredAt).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${typeBadge.className}`}>
                              {typeBadge.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${
                              payment.paymentMethod === 'SEPA' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {payment.paymentMethod || 'SWIFT'}
                            </span>
                            {payment.methodMismatch && <span className="ml-1 text-xs text-amber-700" title="Payment method differs from received-on requisites">⚠</span>}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-700">{payment.receivedOnIban || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{providerName}</td>
                          <td className="px-4 py-3 min-w-[12rem]">
                            {payment.clientId ? (
                              <div className="text-sm text-gray-900">
                                <div className="font-medium">{getClientDisplayName(clientById.get(payment.clientId)) || payment.clientId}</div>
                                <div className="text-xs text-gray-500">{payment.clientAccountId || 'Matched account'}</div>
                              </div>
                            ) : (
                              <span className="text-sm text-orange-700 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded">
                                Unmatched (Suspense)
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{payment.providerReference || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{resolveCurrencyLabel(payment.currencyId) || payment.currencyId}</td>
                          <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                            {formatAmount(payment.amount, resolveCurrencyLabel(payment.currencyId) || payment.currencyId)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[statusKey] || 'bg-gray-100 text-gray-700'}`}>
                              {formatIncomingStatus(payment.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 justify-center flex-wrap">
                              {isDraft && (
                                <>
                                  <button
                                    onClick={() => void handleIncomingSubmitDraftFromList(payment)}
                                    disabled={isLoading}
                                    className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                                  >
                                    {isLoading ? '...' : 'Submit'}
                                  </button>
                                  <button
                                    onClick={() => openEditIncomingDraftModal(payment, isOftIncomingView ? 'digital_incoming' : 'incoming')}
                                    disabled={isLoading}
                                    className="px-2 py-1 bg-amber-500 text-white text-xs rounded hover:bg-amber-600 disabled:opacity-50"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => void handleIncomingDeleteDraft(payment)}
                                    disabled={isLoading}
                                    className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                              {canReview && (
                                <button
                                  onClick={() => void handleIncomingUnderReview(payment.id)}
                                  disabled={isLoading}
                                  className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {isLoading ? '...' : 'Under review'}
                                </button>
                              )}
                              {canApprove && (
                                <button
                                  onClick={() => void handleIncomingApprove(payment.id)}
                                  disabled={isLoading}
                                  className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {isLoading ? '...' : 'Approve'}
                                </button>
                              )}
                              {canReleaseFunds && (
                                <button
                                  onClick={() => void handleIncomingRelease(payment.id)}
                                  disabled={isLoading}
                                  className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                                >
                                  {isLoading ? '...' : 'Release funds'}
                                </button>
                              )}
                              {canReject && (
                                <button
                                  onClick={() => {
                                    setIncomingRejectReason('');
                                    setIncomingRejectModal({ id: payment.id, open: true });
                                  }}
                                  disabled={isLoading}
                                  className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 disabled:opacity-50"
                                >
                                  Reject
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setIncomingDetailSource(isOftIncomingView ? 'oft' : 'incoming');
                                  setIncomingDetailId(payment.id);
                                }}
                                disabled={isLoading}
                                className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
                                title="View incoming payment details"
                              >
                                <PanelRightOpen className="h-3 w-3" />
                                Details
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}

      <OutgoingPaymentDetailModal
        isOpen={detailPaymentId !== null}
        paymentId={detailPaymentId}
        onClose={() => setDetailPaymentId(null)}
        onOpenLedgerLinks={(id) => void handleOpenLinksModal(id)}
        onRegisterReturn={(payment) => {
          setReturnOriginalPaymentId(payment.id);
          setPaymentFlowTab('returns');
        }}
      />

      <IncomingPaymentDetailModal
        isOpen={incomingDetailId !== null}
        paymentId={incomingDetailId}
        onClose={() => {
          setIncomingDetailId(null);
          setIncomingDetailSource('incoming');
        }}
        clients={clients}
        providers={providers}
        countries={countries}
        resolveCurrencyLabel={resolveCurrencyLabel}
        source={incomingDetailSource}
      />

      {createModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {createTab === 'outgoing' && editingDraftPaymentId
                  ? 'Edit outgoing draft'
                  : createTab === 'incoming' && editingIncomingDraftId
                    ? 'Edit incoming draft'
                    : createTab === 'digital_incoming' && editingIncomingDraftId
                      ? 'Edit OFT incoming draft'
                      : 'Create payment'}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setEditingDraftPaymentId(null);
                  setEditingIncomingDraftId(null);
                  setIncomingFeeQuote(null);
                  resetOutgoingRecipientLookupState();
                  setCreateModalOpen(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-5 py-4 border-b border-gray-200">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setCreateTab('outgoing');
                    resetOutgoingRecipientLookupState();
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${createTab === 'outgoing' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  Outgoing
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingDraftPaymentId(null);
                    setEditingIncomingDraftId(null);
                    setIncomingFeeQuote(null);
                    resetOutgoingRecipientLookupState();
                    setCreateTab('incoming');
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${createTab === 'incoming' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  Incoming
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingDraftPaymentId(null);
                    setCreateTab('digital_outgoing');
                    resetOutgoingRecipientLookupState();
                    setOutgoingForm((prev) => ({ ...prev, paymentKind: 'DIGITAL_OUTGOING' }));
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${createTab === 'digital_outgoing' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  OFT Outgoing
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingDraftPaymentId(null);
                    setEditingIncomingDraftId(null);
                    setIncomingFeeQuote(null);
                    setCreateTab('digital_incoming');
                    resetOutgoingRecipientLookupState();
                    setIncomingForm((prev) => ({ ...prev, paymentKind: 'DIGITAL_INCOMING' }));
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${createTab === 'digital_incoming' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  OFT Incoming
                </button>
              </div>
            </div>

            {createTab === 'outgoing' ? (
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <select
                  value={outgoingForm.clientId}
                  onChange={(e) =>
                    {
                      resetOutgoingRecipientLookupState();
                      setOutgoingForm((prev) => ({
                        ...prev,
                        clientId: e.target.value,
                        fromAccountId: '',
                        paymentCurrencyId: '',
                        recipientClientId: '',
                      }));
                    }
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select client</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {getClientOptionLabel(c)}
                    </option>
                  ))}
                </select>

                <select
                  value={outgoingForm.fromAccountId}
                  onChange={(e) => {
                    const accountId = e.target.value;
                    const selected = outgoingAccounts.find((a) => a.id === accountId);
                    setOutgoingForm((prev) => ({
                      ...prev,
                      fromAccountId: accountId,
                      paymentCurrencyId: selected?.currencyId ?? prev.paymentCurrencyId,
                    }));
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Source account</option>
                  {filteredOutgoingAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {[resolveCurrencyLabel(a.currencyId), a.accountNumber, a.bankName].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </select>

                <select
                  value={outgoingForm.paymentCurrencyId}
                  onChange={(e) =>
                    setOutgoingForm((prev) => ({
                      ...prev,
                      paymentCurrencyId: e.target.value,
                      fromAccountId:
                        prev.fromAccountId &&
                        outgoingAccounts.some((a) => a.id === prev.fromAccountId && a.currencyId === e.target.value)
                          ? prev.fromAccountId
                          : '',
                    }))
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Payment currency</option>
                  {outgoingCurrencyOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code}
                    </option>
                  ))}
                </select>

                <select
                  value={outgoingForm.paymentKind}
                  onChange={(e) =>
                    {
                      resetOutgoingRecipientLookupState();
                      setOutgoingForm((prev) => ({
                        ...prev,
                        paymentKind: e.target.value as 'EXTERNAL' | 'INTERNAL',
                        recipientClientId: '',
                      }));
                    }
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="EXTERNAL">External</option>
                  <option value="INTERNAL">Internal</option>
                </select>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={outgoingForm.amount}
                  onChange={(e) => setOutgoingForm((prev) => ({ ...prev, amount: e.target.value }))}
                  placeholder="Amount"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <EnglishDatePicker
                  value={outgoingForm.paymentDate}
                  onChange={(value) => setOutgoingForm((prev) => ({ ...prev, paymentDate: value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <select
                  value={outgoingForm.purpose}
                  onChange={(e) => setOutgoingForm((prev) => ({ ...prev, purpose: e.target.value as PaymentPurposeValue }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {PAYMENT_PURPOSE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  value={outgoingForm.paymentReference}
                  onChange={(e) => setOutgoingForm((prev) => ({ ...prev, paymentReference: e.target.value }))}
                  placeholder="Payment reference"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                {outgoingForm.paymentKind === 'INTERNAL' ? (
                  <div ref={recipientLookupContainerRef} className="relative lg:col-span-2 space-y-2">
                    <label className="block text-xs font-medium text-gray-600">Recipient client</label>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={recipientLookupQuery}
                        onFocus={() => setRecipientLookupOpen(true)}
                        onChange={(e) => {
                          setRecipientLookupOpen(true);
                          setRecipientLookupQuery(e.target.value);
                          if (outgoingForm.recipientClientId) {
                            setOutgoingForm((prev) => ({ ...prev, recipientClientId: '' }));
                          }
                          if (!e.target.value.trim()) {
                            setRecipientLookupResults([]);
                          }
                        }}
                        placeholder="Search by XC-ID, company name or email"
                        autoComplete="off"
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {recipientLookupOpen ? (
                      <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                        {!recipientLookupQuery.trim() || recipientLookupQuery.trim().length < 2 ? (
                          <p className="px-3 py-3 text-sm text-gray-500">Type at least 2 characters to search.</p>
                        ) : recipientLookupLoading ? (
                          <p className="px-3 py-3 text-sm text-gray-500">Searching clients...</p>
                        ) : recipientLookupResults.length === 0 ? (
                          <p className="px-3 py-3 text-sm text-gray-500">No clients found.</p>
                        ) : (
                          <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100">
                            {recipientLookupResults.map((client) => {
                              const displayName = getClientDisplayName(client) || client.email || client.id;
                              const clientPublicId = formatPublicIdDisplay(client.publicId) || 'No XC-ID';
                              const accountLoading = !!recipientAccountLoadingByClientId[client.id];
                              const accounts = recipientAccountsByClientId[client.id] ?? [];
                              const preferredAccount =
                                selectedOutgoingSourceAccount
                                  ? accounts.find(
                                      (account) =>
                                        account.providerId === selectedOutgoingSourceAccount.providerId &&
                                        account.currencyId === selectedOutgoingSourceAccount.currencyId
                                    )
                                  : undefined;
                              const accountForCard = preferredAccount ?? accounts[0];
                              const accountPart = accountLoading
                                ? 'Loading accounts...'
                                : accountForCard
                                  ? `${resolveCurrencyLabel(accountForCard.currencyId) || accountForCard.currencyId}+${providerNameById.get(accountForCard.providerId) || accountForCard.providerId}`
                                  : 'No active accounts';
                              const cardLine = [
                                displayName,
                                clientPublicId,
                                client.email || 'No email',
                                accountPart,
                              ].join(' · ');

                              return (
                                <li
                                  key={client.id}
                                  onMouseDown={() => {
                                    setOutgoingForm((prev) => ({ ...prev, recipientClientId: client.id }));
                                    setRecipientLookupOpen(false);
                                    setRecipientLookupQuery('');
                                    setRecipientLookupResults([]);
                                    void ensureRecipientAccountsLoaded(client.id);
                                  }}
                                  className="px-3 py-2.5 cursor-pointer hover:bg-indigo-50"
                                >
                                  <div className="text-sm text-gray-900 leading-snug break-words">{cardLine}</div>
                                  {preferredAccount ? (
                                    <div className="mt-1 text-[11px] font-medium text-emerald-700">Matches sender currency + provider</div>
                                  ) : null}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                      ) : null}
                    </div>

                    {selectedRecipientClient ? (
                      <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[11px] uppercase tracking-wide text-indigo-600 font-semibold">Selected recipient</p>
                            <p className="text-sm text-gray-900 mt-0.5 break-words">
                              {[
                                getClientDisplayName(selectedRecipientClient) || selectedRecipientClient.email || selectedRecipientClient.id,
                                formatPublicIdDisplay(selectedRecipientClient.publicId) || 'No XC-ID',
                                selectedRecipientClient.email || 'No email',
                                recipientCompatibility.kind === 'compatible'
                                  ? (recipientCompatibility.message.replace(/^Compatible:\s*/i, '') || 'Matched account')
                                  : (() => {
                                      const account = selectedRecipientAccounts[0];
                                      if (!account) return 'No active accounts';
                                      return `${resolveCurrencyLabel(account.currencyId) || account.currencyId}+${providerNameById.get(account.providerId) || account.providerId}`;
                                    })(),
                              ].join(' · ')}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setOutgoingForm((prev) => ({ ...prev, recipientClientId: '' }));
                              setRecipientLookupQuery('');
                              setRecipientLookupResults([]);
                              setRecipientLookupOpen(false);
                            }}
                            className="shrink-0 text-xs font-medium text-indigo-700 hover:text-indigo-900"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    ) : null}

                    <div
                      className={`rounded-lg border px-3 py-2 text-xs ${
                        recipientCompatibility.kind === 'compatible'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : recipientCompatibility.kind === 'incompatible'
                            ? 'border-red-200 bg-red-50 text-red-700'
                            : recipientCompatibility.kind === 'checking'
                              ? 'border-blue-200 bg-blue-50 text-blue-700'
                              : 'border-gray-200 bg-gray-50 text-gray-600'
                      }`}
                    >
                      <span className="font-semibold">Compatibility: </span>
                      {recipientCompatibility.message || 'Select recipient client.'}
                    </div>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={outgoingForm.beneficiaryName}
                      onChange={(e) => setOutgoingForm((prev) => ({ ...prev, beneficiaryName: e.target.value }))}
                      placeholder="Beneficiary name"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      value={outgoingForm.beneficiaryBankName}
                      onChange={(e) => setOutgoingForm((prev) => ({ ...prev, beneficiaryBankName: e.target.value }))}
                      placeholder="Beneficiary bank name"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      value={outgoingForm.beneficiaryAccountNumber}
                      onChange={(e) => setOutgoingForm((prev) => ({ ...prev, beneficiaryAccountNumber: e.target.value }))}
                      placeholder="Account / IBAN"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      value={outgoingForm.beneficiarySwift}
                      onChange={(e) => setOutgoingForm((prev) => ({ ...prev, beneficiarySwift: e.target.value.toUpperCase() }))}
                      placeholder="SWIFT code"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <select
                      value={outgoingForm.recipientCountryId}
                      onChange={(e) =>
                        setOutgoingForm((prev) => ({
                          ...prev,
                          recipientCountryId: e.target.value,
                          bankCountryId: prev.bankCountryId || e.target.value,
                        }))
                      }
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Recipient country</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={outgoingForm.bankCountryId}
                      onChange={(e) => setOutgoingForm((prev) => ({ ...prev, bankCountryId: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Bank country</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={outgoingForm.beneficiaryCity}
                      onChange={(e) => setOutgoingForm((prev) => ({ ...prev, beneficiaryCity: e.target.value }))}
                      placeholder="City (optional)"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      value={outgoingForm.beneficiaryZipCode}
                      onChange={(e) => setOutgoingForm((prev) => ({ ...prev, beneficiaryZipCode: e.target.value }))}
                      placeholder="ZIP code (optional)"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      value={outgoingForm.beneficiaryAddress}
                      onChange={(e) => setOutgoingForm((prev) => ({ ...prev, beneficiaryAddress: e.target.value }))}
                      placeholder="Address (optional)"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 lg:col-span-2"
                    />
                  </>
                )}

                <div className="lg:col-span-3 flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingDraftPaymentId(null);
                      resetOutgoingRecipientLookupState();
                      setCreateModalOpen(false);
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void submitOutgoingCreate()}
                    disabled={createLoading || hasRecipientCompatibilityIssue}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {createLoading
                      ? (editingDraftPaymentId ? 'Saving...' : 'Creating...')
                      : (editingDraftPaymentId ? 'Save draft changes' : 'Create outgoing draft')}
                  </button>
                </div>
              </div>
            ) : createTab === 'incoming' ? (
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="lg:col-span-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIncomingMode('matched')}
                    className={`px-3 py-1.5 text-sm rounded-lg ${incomingMode === 'matched' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Matched to client
                  </button>
                  {!incomingForm.clientId && (
                    <button
                      type="button"
                      onClick={() => setIncomingMode('unmatched')}
                      className={`px-3 py-1.5 text-sm rounded-lg ${incomingMode === 'unmatched' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                    >
                      Unmatched (suspense)
                    </button>
                  )}
                </div>

                {incomingMode === 'matched' ? (
                  <>
                    <select
                      value={incomingForm.clientId}
                      onChange={(e) =>
                        setIncomingForm((prev) => ({ ...prev, clientId: e.target.value, clientAccountId: '' }))
                      }
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select client</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {getClientOptionLabel(c)}
                        </option>
                      ))}
                    </select>

                    <select
                      value={incomingForm.clientAccountId}
                      onChange={(e) => setIncomingForm((prev) => ({
                        ...prev,
                        clientAccountId: e.target.value,
                        receivedOnRequisiteId: '',
                        paymentMethod: 'SWIFT',
                        methodMismatchReason: '',
                      }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Destination account</option>
                      {incomingAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {[resolveCurrencyLabel(a.currencyId), a.accountNumber, a.bankName].filter(Boolean).join(' · ')}
                        </option>
                      ))}
                    </select>

                    {incomingForm.clientAccountId ? (
                      <div className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-600 bg-gray-50">
                        {(() => {
                          const selected = incomingAccounts.find((a) => a.id === incomingForm.clientAccountId);
                          if (!selected) return 'Provider/Currency will be resolved from account.';
                          const providerName =
                            providers.find((p) => p.id === selected.providerId)?.name ?? selected.providerId;
                          const currencyCode = resolveCurrencyLabel(selected.currencyId) || selected.currencyId;
                          return `Provider: ${providerName} · Currency: ${currencyCode}`;
                        })()}
                      </div>
                    ) : (
                      <div />
                    )}

                    <select
                      value={incomingForm.receivedOnRequisiteId}
                      onChange={(e) => {
                        const requisite = incomingRequisites.find((item) => item.id === e.target.value);
                        setIncomingForm((prev) => ({
                          ...prev,
                          receivedOnRequisiteId: e.target.value,
                          paymentMethod: requisite?.paymentMethod === 'SEPA' ? 'SEPA' : 'SWIFT',
                          methodMismatchReason: '',
                        }));
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Received on requisites</option>
                      {incomingRequisites.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.paymentMethod} · {item.iban || item.accountNumber || item.accountName || item.id}
                        </option>
                      ))}
                    </select>
                    <select
                      value={incomingForm.paymentMethod}
                      onChange={(e) => setIncomingForm((prev) => ({
                        ...prev,
                        paymentMethod: e.target.value === 'SEPA' ? 'SEPA' : 'SWIFT',
                      }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="SWIFT">SWIFT</option>
                      <option value="SEPA">SEPA</option>
                    </select>
                    {(() => {
                      const requisite = incomingRequisites.find((item) => item.id === incomingForm.receivedOnRequisiteId);
                      const mismatch = Boolean(requisite && requisite.paymentMethod !== incomingForm.paymentMethod);
                      if (!mismatch) return <div />;
                      return (
                        <div className="col-span-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                          Selected payment method does not match the received-on requisites.
                          <input
                            value={incomingForm.methodMismatchReason}
                            onChange={(e) => setIncomingForm((prev) => ({ ...prev, methodMismatchReason: e.target.value }))}
                            placeholder="Reason is required"
                            className="mt-2 w-full rounded-lg border border-amber-300 px-3 py-2 text-sm"
                          />
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    <select
                      value={incomingForm.providerId}
                      onChange={(e) => setIncomingForm((prev) => ({ ...prev, providerId: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select provider</option>
                      {providers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={incomingForm.currencyId}
                      onChange={(e) => setIncomingForm((prev) => ({ ...prev, currencyId: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select currency</option>
                      {currencies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.code}
                        </option>
                      ))}
                    </select>
                    <div />
                  </>
                )}

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={incomingForm.amount}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, amount: e.target.value }))}
                  placeholder="Amount"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <EnglishDatePicker
                  value={incomingForm.paymentDate}
                  onChange={(value) => setIncomingForm((prev) => ({ ...prev, paymentDate: value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <select
                  value={incomingForm.paymentKind}
                  onChange={(e) =>
                    setIncomingForm((prev) => ({
                      ...prev,
                      paymentKind: e.target.value as 'EXTERNAL' | 'INTERNAL',
                    }))
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="EXTERNAL">External</option>
                  <option value="INTERNAL">Internal</option>
                </select>

                <input
                  type="text"
                  value={incomingForm.paymentReference}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, paymentReference: e.target.value }))}
                  placeholder="Payment reference"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <input
                  type="text"
                  value={incomingForm.beneficiaryName}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, beneficiaryName: e.target.value }))}
                  placeholder="Beneficiary name"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={incomingForm.beneficiaryBankName}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, beneficiaryBankName: e.target.value }))}
                  placeholder="Beneficiary bank name"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={incomingForm.beneficiaryAccountNumber}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, beneficiaryAccountNumber: e.target.value }))}
                  placeholder="Account / IBAN"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={incomingForm.beneficiarySwift}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, beneficiarySwift: e.target.value.toUpperCase() }))}
                  placeholder="SWIFT code"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={incomingForm.recipientCountryId}
                  onChange={(e) =>
                    setIncomingForm((prev) => ({
                      ...prev,
                      recipientCountryId: e.target.value,
                      bankCountryId: prev.bankCountryId || e.target.value,
                    }))
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Recipient country</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
                <select
                  value={incomingForm.bankCountryId}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, bankCountryId: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 lg:col-span-2"
                >
                  <option value="">Bank country</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={incomingForm.beneficiaryCity}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, beneficiaryCity: e.target.value }))}
                  placeholder="City (optional)"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={incomingForm.beneficiaryZipCode}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, beneficiaryZipCode: e.target.value }))}
                  placeholder="ZIP code (optional)"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={incomingForm.beneficiaryAddress}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, beneficiaryAddress: e.target.value }))}
                  placeholder="Address (optional)"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 lg:col-span-2"
                />

                <input
                  type="text"
                  value={incomingForm.backofficeComment}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, backofficeComment: e.target.value }))}
                  placeholder="Backoffice comment (optional)"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 lg:col-span-3"
                />

                <div className="lg:col-span-3 p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
                  <div className="text-sm font-medium text-indigo-900 mb-1">Fee preview</div>
                  {incomingFeeQuoteLoading ? (
                    <p className="text-sm text-indigo-700">Calculating fee...</p>
                  ) : incomingFeeQuote ? (
                    <div className="text-sm text-indigo-900 flex flex-wrap gap-x-6 gap-y-1">
                      <span>
                        Gross:{' '}
                        {formatAmount(
                          incomingFeeQuote.grossAmount,
                          resolveCurrencyLabel(incomingFeeQuote.currencyId) || incomingFeeQuote.currencyId,
                        )}
                      </span>
                      <span>
                        Fee:{' '}
                        {formatAmount(
                          incomingFeeQuote.feeAmount,
                          resolveCurrencyLabel(incomingFeeQuote.currencyId) || incomingFeeQuote.currencyId,
                        )}
                      </span>
                      <span>
                        Net:{' '}
                        {formatAmount(
                          incomingFeeQuote.netAmount,
                          resolveCurrencyLabel(incomingFeeQuote.currencyId) || incomingFeeQuote.currencyId,
                        )}
                      </span>
                    </div>
                  ) : (
                    <p className="text-sm text-indigo-700">Enter amount and account details to preview fee.</p>
                  )}
                </div>

                {editingIncomingDraftId && (
                  <div className="lg:col-span-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 mb-2">Fee override</div>
                    <div className="flex flex-wrap gap-2 items-end">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={incomingOverrideFeeAmount}
                        onChange={(e) => setIncomingOverrideFeeAmount(e.target.value)}
                        placeholder="New fee amount"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
                      />
                      <input
                        type="text"
                        value={incomingOverrideFeeReason}
                        onChange={(e) => setIncomingOverrideFeeReason(e.target.value)}
                        placeholder="Override reason"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 min-w-[12rem]"
                      />
                      <button
                        type="button"
                        onClick={() => void handleIncomingOverrideFee()}
                        disabled={createLoading || !incomingOverrideFeeReason.trim()}
                        className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                      >
                        Apply override
                      </button>
                    </div>
                  </div>
                )}

                <div className="lg:col-span-3 flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingIncomingDraftId(null);
                      setIncomingFeeQuote(null);
                      setCreateModalOpen(false);
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void saveIncomingDraft()}
                    disabled={createLoading}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {createLoading
                      ? (editingIncomingDraftId ? 'Saving...' : 'Saving...')
                      : (editingIncomingDraftId ? 'Save draft changes' : 'Save draft')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void submitIncomingDraft()}
                    disabled={createLoading}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {createLoading ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </div>
            ) : createTab === 'digital_outgoing' ? (
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <select
                  value={outgoingForm.clientId}
                  onChange={(e) =>
                    setOutgoingForm((prev) => ({
                      ...prev,
                      clientId: e.target.value,
                      fromAccountId: '',
                      paymentCurrencyId: '',
                    }))
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select client</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {getClientOptionLabel(c)}
                    </option>
                  ))}
                </select>

                <select
                  value={outgoingForm.fromAccountId}
                  onChange={(e) => {
                    const accountId = e.target.value;
                    const selected = outgoingAccounts.find((a) => a.id === accountId);
                    setOutgoingForm((prev) => ({
                      ...prev,
                      fromAccountId: accountId,
                      paymentCurrencyId: selected?.currencyId ?? prev.paymentCurrencyId,
                    }));
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Source account</option>
                  {filteredOutgoingAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {[resolveCurrencyLabel(a.currencyId), a.accountNumber, a.bankName].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={outgoingForm.amount}
                  onChange={(e) => setOutgoingForm((prev) => ({ ...prev, amount: e.target.value }))}
                  placeholder="Amount"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <EnglishDatePicker
                  value={outgoingForm.paymentDate}
                  onChange={(value) => setOutgoingForm((prev) => ({ ...prev, paymentDate: value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <input
                  type="text"
                  value={outgoingForm.paymentReference}
                  onChange={(e) => setOutgoingForm((prev) => ({ ...prev, paymentReference: e.target.value }))}
                  placeholder="Payment reference"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 lg:col-span-2"
                />

                <div className="lg:col-span-3 flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingDraftPaymentId(null);
                      setCreateModalOpen(false);
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void submitOutgoingCreate()}
                    disabled={createLoading}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {createLoading ? 'Creating...' : 'Create OFT outgoing'}
                  </button>
                </div>
              </div>
            ) : createTab === 'digital_incoming' ? (
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <select
                  value={incomingForm.clientId}
                  onChange={(e) =>
                    setIncomingForm((prev) => ({ ...prev, clientId: e.target.value, clientAccountId: '' }))
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select client</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {getClientOptionLabel(c)}
                    </option>
                  ))}
                </select>

                <select
                  value={incomingForm.clientAccountId}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, clientAccountId: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Destination account</option>
                  {incomingAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {[resolveCurrencyLabel(a.currencyId), a.accountNumber, a.bankName].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={incomingForm.amount}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, amount: e.target.value }))}
                  placeholder="Amount"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <EnglishDatePicker
                  value={incomingForm.paymentDate}
                  onChange={(value) => setIncomingForm((prev) => ({ ...prev, paymentDate: value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <input
                  type="text"
                  value={incomingForm.paymentReference}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, paymentReference: e.target.value }))}
                  placeholder="Payment reference"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 lg:col-span-2"
                />

                <input
                  type="text"
                  value={incomingForm.backofficeComment}
                  onChange={(e) => setIncomingForm((prev) => ({ ...prev, backofficeComment: e.target.value }))}
                  placeholder="Backoffice comment (optional)"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 lg:col-span-3"
                />

                <div className="lg:col-span-3 p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
                  <div className="text-sm font-medium text-indigo-900 mb-1">Fee preview</div>
                  {incomingFeeQuoteLoading ? (
                    <p className="text-sm text-indigo-700">Calculating fee...</p>
                  ) : incomingFeeQuote ? (
                    <div className="text-sm text-indigo-900 flex flex-wrap gap-x-6 gap-y-1">
                      <span>
                        Gross:{' '}
                        {formatAmount(
                          incomingFeeQuote.grossAmount,
                          resolveCurrencyLabel(incomingFeeQuote.currencyId) || incomingFeeQuote.currencyId,
                        )}
                      </span>
                      <span>
                        Fee:{' '}
                        {formatAmount(
                          incomingFeeQuote.feeAmount,
                          resolveCurrencyLabel(incomingFeeQuote.currencyId) || incomingFeeQuote.currencyId,
                        )}
                      </span>
                      <span>
                        Net:{' '}
                        {formatAmount(
                          incomingFeeQuote.netAmount,
                          resolveCurrencyLabel(incomingFeeQuote.currencyId) || incomingFeeQuote.currencyId,
                        )}
                      </span>
                    </div>
                  ) : (
                    <p className="text-sm text-indigo-700">Enter amount and account to preview fee.</p>
                  )}
                </div>

                {editingIncomingDraftId && (
                  <div className="lg:col-span-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 mb-2">Fee override</div>
                    <div className="flex flex-wrap gap-2 items-end">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={incomingOverrideFeeAmount}
                        onChange={(e) => setIncomingOverrideFeeAmount(e.target.value)}
                        placeholder="New fee amount"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
                      />
                      <input
                        type="text"
                        value={incomingOverrideFeeReason}
                        onChange={(e) => setIncomingOverrideFeeReason(e.target.value)}
                        placeholder="Override reason"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 min-w-[12rem]"
                      />
                      <button
                        type="button"
                        onClick={() => void handleIncomingOverrideFee()}
                        disabled={createLoading || !incomingOverrideFeeReason.trim()}
                        className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                      >
                        Apply override
                      </button>
                    </div>
                  </div>
                )}

                <div className="lg:col-span-3 flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingIncomingDraftId(null);
                      setIncomingFeeQuote(null);
                      setCreateModalOpen(false);
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void saveIncomingDraft()}
                    disabled={createLoading}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {createLoading
                      ? (editingIncomingDraftId ? 'Saving...' : 'Saving...')
                      : (editingIncomingDraftId ? 'Save draft changes' : 'Save draft')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void submitIncomingDraft()}
                    disabled={createLoading}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {createLoading ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Reject Payment</h3>
              <button onClick={() => setRejectModal({ id: '', open: false })} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                <input
                  type="text"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Reason for rejection"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Comment</label>
                <textarea
                  value={rejectComment}
                  onChange={(e) => setRejectComment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  rows={3}
                  placeholder="Additional comments"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setRejectModal({ id: '', open: false })}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {incomingRejectModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Reject Incoming Payment</h3>
              <button
                onClick={() => {
                  setIncomingRejectModal({ id: '', open: false });
                  setIncomingRejectReason('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                <input
                  type="text"
                  value={incomingRejectReason}
                  onChange={(e) => setIncomingRejectReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Reason for incoming rejection"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setIncomingRejectModal({ id: '', open: false });
                  setIncomingRejectReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleIncomingReject()}
                disabled={!incomingRejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hold Modal */}
      {holdModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Put Payment On Hold</h3>
              <button onClick={() => setHoldModal({ id: '', open: false })} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                <input
                  type="text"
                  value={holdReason}
                  onChange={(e) => setHoldReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Reason for hold"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Comment</label>
                <textarea
                  value={holdComment}
                  onChange={(e) => setHoldComment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  rows={3}
                  placeholder="Additional comments"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setHoldModal({ id: '', open: false })}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handlePutOnHold}
                disabled={!holdReason.trim()}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm disabled:opacity-50"
              >
                Put On Hold
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Payment Modal (back-office) — returns principal + fee to the client */}
      {cancelModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Cancel Payment</h3>
              <button onClick={() => setCancelModal({ id: '', open: false })} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              The payment will be cancelled and both the principal amount and the reserved fee will be returned to the client.
              This is different from a reject.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                rows={3}
                placeholder="Optional reason for cancellation"
              />
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setCancelModal({ id: '', open: false })}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
              >
                Close
              </button>
              <button
                onClick={() => void handleCancelPayment()}
                disabled={actionLoading === cancelModal.id}
                className="flex-1 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 text-sm disabled:opacity-50"
              >
                {actionLoading === cancelModal.id ? '...' : 'Cancel payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ledger Links Modal (Audit Trail) */}
      {linksModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Ledger Transaction Links (Audit Trail)</h3>
              <button onClick={() => setLinksModal({ id: '', open: false })} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {linksLoading ? (
              <div className="text-center py-8 text-gray-500">Loading ledger links...</div>
            ) : ledgerLinks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No ledger transaction links found for this payment.</div>
            ) : (
              <div className="space-y-2">
                {ledgerLinks.map((link) => (
                  <div key={link.id} className="flex items-center gap-3 text-sm bg-gray-50 p-3 rounded border">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium whitespace-nowrap">
                      {link.linkType}
                    </span>
                    <span className="text-gray-500 text-xs whitespace-nowrap">
                      {new Date(link.createdAt).toLocaleString()}
                    </span>
                    <span className="text-gray-700 flex-1">
                      {link.description || link.linkType}
                    </span>
                    <div className="text-xs text-gray-400 font-mono text-right">
                      <div>From: {link.sourceTransactionId.slice(0, 8)}...</div>
                      <div>To: {link.linkedTransactionId.slice(0, 8)}...</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setLinksModal({ id: '', open: false })}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentsPage;
